import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { trpc } from "../lib/trpc.js";
import { useTaskSSE, type TaskEvent } from "../hooks/useTaskSSE.js";
import { RichMarkdown, TaskAttachments } from "../components/RichContent.js";
import { CreateFunnelModal } from "../components/brain/CreateFunnelModal.js";
import { CreateCreativesModal } from "../components/brain/CreateCreativesModal.js";
import { useVoice } from "../hooks/useVoice.js";
import { useSandbox } from "../hooks/useSandbox.js";
import { SandboxPanel, AgentModePrompt } from "../components/sandbox/index.js";
import { useAgentSSE, type AgentEvent } from "../hooks/useAgentSSE.js";
import { VoiceButton, VoiceStatusBar } from "../components/VoiceButton.js";
import { OfficeView } from "../components/office/index.js";
import { useOfficeWS } from "../hooks/useOfficeWS.js";
import {
  BrainCircuit, Send, Loader2, RefreshCw, Clock, Zap, AlertCircle,
  CheckCircle2, Radio, ChevronDown, ChevronUp, ArrowLeft, MessageCircle,
  User, Bot, Sparkles, CornerDownLeft, Globe, BarChart3, Terminal,
  Activity, Palette, Plus, Trash2, MessageSquare, Cpu, ChevronRight,
  Mic, Volume2, Plug, Play, Target, Wrench, GitBranch, ToggleLeft, ToggleRight,
  Square, ExternalLink, Shield, ShieldAlert, FileText,
  Upload, Paperclip, Settings2, BookOpen, Layers,
  Database, Edit3, Save, X, Eye, EyeOff,
  Smartphone, Hash, ListFilter,
  Monitor,
  Building2, Users,
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────

const AVAILABLE_MODELS = [
  { id: "auto", label: "Auto (Inteligente)", provider: "YOS Megan", badge: "Recomendado", description: "Seleciona o melhor modelo para cada prompt" },
  // ── Modelos Premium (Março 2026) ──
  { id: "gpt-5.4", label: "GPT-5.4", provider: "OpenAI", badge: "Top", description: "Raciocínio avançado e tarefas complexas (256K ctx)" },
  { id: "gpt-5.4-mini", label: "GPT-5.4 Mini", provider: "OpenAI", badge: "Rápido", description: "Rápido e potente para tarefas gerais (256K ctx)" },
  { id: "claude-opus-4-6-20260313", label: "Claude Opus 4.6", provider: "Anthropic", badge: "Top", description: "#1 em coding e agentic tasks (1M ctx)" },
  { id: "claude-sonnet-4-6-20260313", label: "Claude Sonnet 4.6", provider: "Anthropic", badge: "Avançado", description: "Escrita criativa e análise detalhada (1M ctx)" },
  { id: "gemini-3.1-pro", label: "Gemini 3.1 Pro", provider: "Google", badge: "Avançado", description: "Análise de dados e multimodal (2M ctx)" },
  // ── Modelos Utilitários ──
  { id: "gpt-5.4-nano", label: "GPT-5.4 Nano", provider: "OpenAI", badge: "Leve", description: "Ultra-rápido para tarefas simples" },
  { id: "deepseek-chat", label: "DeepSeek V3.2", provider: "DeepSeek", badge: "Budget", description: "Open-source poderoso e econômico" },
  { id: "deepseek-reasoner", label: "DeepSeek R1", provider: "DeepSeek", badge: "Raciocínio", description: "Raciocínio avançado open-source" },
  // ── Geração de Mídia ──
  { id: "nano-banana-2", label: "Nano Banana 2", provider: "Nano Banana", badge: "Imagem", description: "Geração de imagens de alta qualidade" },
  { id: "nano-banana-pro", label: "Nano Banana Pro", provider: "Nano Banana", badge: "Imagem/Vídeo", description: "Imagens e vídeos profissionais" },
];

const AVAILABLE_CONNECTORS = [
  { id: "github", label: "GitHub", icon: GitBranch, description: "Repos, PRs, Issues, Code" },
  { id: "railway", label: "Railway", icon: Activity, description: "Projetos, Deploys, Logs" },
  { id: "web_search", label: "Web Search", icon: Globe, description: "Pesquisa na internet" },
  { id: "database", label: "Database", icon: Cpu, description: "Consultas SQL diretas" },
  { id: "sandbox", label: "Sandbox", icon: Terminal, description: "Terminal + Browser Linux" },
];

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
  running: "bg-primary/10 text-primary border-primary/20",
  completed: "bg-success/10 text-success border-success/20",
  failed: "bg-destructive/10 text-destructive border-destructive/20",
};

const statusLabels: Record<string, string> = {
  pending: "Pendente", running: "Executando", completed: "Concluído", failed: "Falhou",
};

const statusIcons: Record<string, typeof Loader2> = {
  pending: Clock, running: Loader2, completed: CheckCircle2, failed: AlertCircle,
};

// ─── Detect if message needs agent mode ──────────────────────────────────────
function shouldUseAgentMode(message: string): boolean {
  const lower = message.toLowerCase();
  const agentPatterns = [
    /\b(verifique|verificar|cheque|checar)\b.*\b(railway|github|deploy|serviço|serviços|status)\b/,
    /\b(analise|analisar)\b.*\b(nicho|mercado|performance|dados|sites?)\b/,
    /\b(crie|criar|deploy|deploye)\b.*\b(site|funil|funnel|landing)\b/,
    /\b(liste|listar)\b.*\b(projetos?|serviços?|repos?|sites?)\b/,
    /\b(pesquise|pesquisar|research)\b.*\b(nicho|mercado|keyword|tendência)\b/,
    /\b(execute|executar|rode|rodar)\b.*\b(auditoria|análise|scan|check|pipeline|workflow)\b/,
    /\b(redeploy|restart|reiniciar)\b/,
    /\b(otimize|otimizar|optimize)\b/,
    /\b(gere|gerar)\b.*\b(conteúdo|seo|artigo|blog)\b.*\b(para|dos?|das?)\b.*\b(sites?|todos?)\b/,
    /\b(qual|quais)\b.*\b(última|ultimo|ação|ações|agente)\b/,
  ];
  return agentPatterns.some((p) => p.test(lower));
}

type BrainTab = "chat" | "office" | "connectors";

// ─── Model Selector Dropdown ─────────────────────────────────────────────────

function ModelSelector({ value, onChange }: { value: string; onChange: (model: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = AVAILABLE_MODELS.find((m) => m.id === value) || AVAILABLE_MODELS[0];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-secondary/50 hover:bg-secondary text-xs text-muted-foreground hover:text-foreground transition-all"
      >
        <Cpu className="w-3.5 h-3.5 text-primary" />
        <span className="font-medium">{selected.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-card border border-border rounded-xl shadow-xl z-50 py-1 max-h-80 overflow-y-auto">
          {AVAILABLE_MODELS.map((model) => (
            <button
              key={model.id}
              onClick={() => { onChange(model.id); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-secondary/70 transition-colors ${
                model.id === value ? "bg-primary/5" : ""
              } ${model.id === "auto" ? "border-b border-border mb-1" : ""}`}
            >
              {model.id === "auto" && (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${model.id === value ? "text-primary" : "text-foreground"}`}>{model.label}</p>
                <p className="text-[10px] text-muted-foreground">{model.description || model.provider}</p>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                model.badge === "Recomendado" ? "bg-primary/10 text-primary border-primary/20" :
                model.badge === "Rápido" ? "bg-success/10 text-success border-success/20" :
                model.badge === "Avançado" ? "bg-blue-500/10 text-blue-500 border-blue-500/20" :
                model.badge === "Budget" ? "bg-yellow-500/10 text-yellow-500 border-yellow-500/20" :
                model.badge === "Raciocínio" ? "bg-purple-500/10 text-purple-500 border-purple-500/20" :
                model.badge === "Imagem" ? "bg-pink-500/10 text-pink-500 border-pink-500/20" :
                model.badge === "Imagem/Vídeo" ? "bg-rose-500/10 text-rose-500 border-rose-500/20" :
                "bg-purple-500/10 text-purple-500 border-purple-500/20"
              }`}>
                {model.badge}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Agent Step Display (inline in chat) ─────────────────────────────────────

function AgentStepInline({ events, isRunning, streamingText, fallbackContent }: { events: AgentEvent[]; isRunning: boolean; streamingText?: string; fallbackContent?: string }) {
  const [showSteps, setShowSteps] = useState(false);

  // Group events by step
  const stepGroups = useMemo(() => {
    const groups: Map<number, AgentEvent[]> = new Map();
    for (const ev of events) {
      const step = ev.step ?? -1;
      if (step >= 0 && ev.type !== "started" && ev.type !== "plan") {
        if (!groups.has(step)) groups.set(step, []);
        groups.get(step)!.push(ev);
      }
    }
    return groups;
  }, [events]);

  const doneEvent = events.find((e) => e.type === "done") as AgentEvent | undefined;
  const finalAnswerEvent = events.find((e) => e.type === "final_answer");
  // Also check for 'content' events from chat/tools/stream
  const contentEvent = events.find((e) => e.type === "content" && (e as any).content);
  const totalSteps = stepGroups.size;
  const completedSteps = Array.from(stepGroups.values()).filter(
    (evts) => evts.some((e) => e.type === "tool_result" || e.type === "final_answer")
  ).length;

  if (events.length === 0 && !isRunning && !fallbackContent) return null;

  // Helper: get a human-readable description for a step
  const getStepLabel = (stepEvents: AgentEvent[]): string => {
    const toolStart = stepEvents.find((e) => e.type === "tool_start");
    const thought = stepEvents.find((e) => e.type === "thought");
    if (toolStart?.tool) {
      const connector = toolStart.connector ? `${toolStart.connector}.` : "";
      return `${connector}${toolStart.tool}`;
    }
    if (thought?.thought) {
      const t = thought.thought;
      const firstSentence = t.split(/[.!\n]/)[0]?.trim();
      return firstSentence && firstSentence.length < 80 ? firstSentence : t.slice(0, 60) + "...";
    }
    return "Processando...";
  };

  // Helper: get icon for tool type
  const getToolIcon = (tool?: string, connector?: string): typeof Terminal => {
    if (connector === "github" || tool?.includes("github")) return GitBranch;
    if (connector === "railway" || tool?.includes("railway") || tool?.includes("deploy")) return Activity;
    if (connector === "web_search" || tool?.includes("search") || tool?.includes("web")) return Globe;
    if (connector === "database" || tool?.includes("sql") || tool?.includes("query")) return Database;
    if (connector === "sandbox" || tool?.includes("shell") || tool?.includes("exec")) return Terminal;
    if (tool?.includes("file") || tool?.includes("read") || tool?.includes("write")) return FileText;
    if (tool?.includes("final_answer")) return CheckCircle2;
    return Wrench;
  };

  // Get current active step info for compact display
  const lastStepEvents = Array.from(stepGroups.values()).pop();
  const currentLabel = lastStepEvents ? getStepLabel(lastStepEvents) : "Analisando...";
  const currentToolStart = lastStepEvents?.find((e) => e.type === "tool_start");
  const CurrentIcon = getToolIcon(currentToolStart?.tool, currentToolStart?.connector);

  const isDone = !!doneEvent;
  const answerText = finalAnswerEvent?.answer || doneEvent?.finalAnswer || doneEvent?.answer || (contentEvent as any)?.content || fallbackContent || "";
  const hasAnswer = !!answerText;

  return (
    <div className="w-full">
      {/* Compact process bar — always visible when there are steps */}
      {totalSteps > 0 && (
        <button
          onClick={() => setShowSteps(!showSteps)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-secondary/40 hover:bg-secondary/60 border border-border/50 transition-all mb-2 group"
        >
          {/* Status icon */}
          {isRunning && !isDone ? (
            <Loader2 className="w-3.5 h-3.5 text-primary animate-spin shrink-0" />
          ) : isDone ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
          ) : (
            <CurrentIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          )}

          {/* Progress info */}
          <div className="flex-1 min-w-0 text-left">
            <span className="text-[11px] text-muted-foreground">
              {isRunning && !isDone ? (
                <>{currentLabel}</>  
              ) : (
                <>Concluído em {doneEvent?.latencyMs ? `${(doneEvent.latencyMs / 1000).toFixed(1)}s` : "--"}</>  
              )}
            </span>
          </div>

          {/* Step count + chevron */}
          <div className="flex items-center gap-1.5 shrink-0">
            {isRunning && !isDone && (
              <div className="w-12 h-1 bg-muted/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary/60 rounded-full transition-all duration-500"
                  style={{ width: `${Math.max((completedSteps / Math.max(totalSteps, 1)) * 100, 8)}%` }}
                />
              </div>
            )}
            <span className="text-[9px] font-mono text-muted-foreground/60">
              {completedSteps}/{totalSteps}
            </span>
            <ChevronDown className={`w-3 h-3 text-muted-foreground/40 transition-transform ${showSteps ? "rotate-180" : ""}`} />
          </div>
        </button>
      )}

      {/* Running indicator (when no steps yet) */}
      {isRunning && !isDone && totalSteps === 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-secondary/40 border border-border/50 mb-2">
          <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />
          <span className="text-[11px] text-muted-foreground">Analisando tarefa...</span>
        </div>
      )}

      {/* Expanded steps — collapsible */}
      {showSteps && totalSteps > 0 && (
        <div className="mb-2 px-1 space-y-px">
          {Array.from(stepGroups.entries()).map(([stepNum, stepEvents]) => {
            const toolStart = stepEvents.find((e) => e.type === "tool_start");
            const toolResult = stepEvents.find((e) => e.type === "tool_result");
            const thought = stepEvents.find((e) => e.type === "thought");
            const isFinalAnswer = stepEvents.some((e) => e.type === "final_answer");
            const isStepActive = isRunning && !toolResult && !isFinalAnswer;
            const isError = toolResult?.status === "error";
            const isStepDone = !!toolResult || isFinalAnswer;
            const ToolIcon = getToolIcon(toolStart?.tool, toolStart?.connector);
            const label = getStepLabel(stepEvents);

            return (
              <div key={stepNum} className="flex items-start gap-2 py-1 px-2">
                {/* Timeline dot */}
                <div className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                  isError ? "bg-destructive"
                  : isStepDone ? "bg-emerald-500"
                  : isStepActive ? "bg-primary animate-pulse"
                  : "bg-muted-foreground/30"
                }`} />

                {/* Step info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <ToolIcon className="w-3 h-3 text-muted-foreground/60 shrink-0" />
                    <span className="text-[10px] font-mono text-muted-foreground truncate">{label}</span>
                    {toolResult?.executionTime && (
                      <span className="text-[8px] text-muted-foreground/40 shrink-0">
                        {(toolResult.executionTime / 1000).toFixed(1)}s
                      </span>
                    )}
                    {isStepActive && <Loader2 className="w-2.5 h-2.5 text-primary animate-spin shrink-0" />}
                    {isError && <AlertCircle className="w-2.5 h-2.5 text-destructive shrink-0" />}
                  </div>
                  {/* Show thought preview on hover-like area */}
                  {thought?.thought && (
                    <p className="text-[9px] text-muted-foreground/50 truncate mt-0.5 leading-tight">
                      {thought.thought.split(/[.!\n]/)[0]?.trim().slice(0, 100)}
                    </p>
                  )}
                  {toolResult?.preview && (
                    <p className="text-[9px] text-muted-foreground/40 truncate mt-0.5">
                      {toolResult.preview.slice(0, 120)}
                    </p>
                  )}
                  {toolResult?.error && (
                    <p className="text-[9px] text-destructive/70 truncate mt-0.5">{toolResult.error.slice(0, 100)}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Streaming text (tokens being generated) */}
      {isRunning && streamingText && !isDone && (
        <div className="mt-1">
          <div className="text-sm text-foreground/90 leading-relaxed">
            <RichMarkdown content={streamingText} />
            <span className="inline-block w-1.5 h-4 bg-primary/60 animate-pulse ml-0.5 align-text-bottom" />
          </div>
        </div>
      )}

      {/* Final Answer */}
      {hasAnswer && (
        <div className="text-sm text-foreground leading-relaxed">
          <RichMarkdown content={answerText} />
        </div>
      )}

      {/* Error */}
      {events.some((e) => e.type === "error") && !hasAnswer && (
        <div className="flex items-center gap-2 px-3 py-2 bg-destructive/5 border border-destructive/20 rounded-lg mt-1">
          <AlertCircle className="w-3.5 h-3.5 text-destructive shrink-0" />
          <span className="text-xs text-destructive">{events.find((e) => e.type === "error")?.message}</span>
        </div>
      )}
    </div>
  );
}

// ─── Unified Chat Tab (Chat + Agent Mode) ────────────────────────────────────

function ChatTab({ selectedModel, onModelChange }: { selectedModel: string; onModelChange: (m: string) => void }) {
  const [input, setInput] = useState("");
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeSource, setActiveSource] = useState<"web_chat" | "telegram" | "whatsapp" | "manus_task">("web_chat");
  const [channelFilter, setChannelFilter] = useState<"all" | "web_chat" | "telegram" | "whatsapp" | "manus_task">("all");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const utils = trpc.useUtils();
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<Array<{ id: string; role: "user" | "assistant"; content: string; createdAt: number }>>([]);

  // ─── Agent Mode State ─────────────────────────────────────────────────
  const [agentMode, setAgentMode] = useState(true); // Agente ativo por padrão
  const [autoAgentDetected, setAutoAgentDetected] = useState(false);
  const [enabledConnectors, setEnabledConnectors] = useState<Set<string>>(new Set(["github", "railway", "web_search", "database", "sandbox"]));
  const [showConnectorPanel, setShowConnectorPanel] = useState(true); // Conectores visíveis por padrão
  const [agentMessages, setAgentMessages] = useState<Array<{ role: "user" | "agent"; content: string; events?: AgentEvent[]; streamingText?: string; isRunning?: boolean; timestamp: number }>>([]);
  // ─── Sandbox Mode State ─────────────────────────────────────────────────
  const sandbox = useSandbox();
  const [showSandboxPrompt, setShowSandboxPrompt] = useState(false);
  const [sandboxReason, setSandboxReason] = useState<string>("");

  // ─── Task Mode State (unified from TasksTab) ──────────────────────────────
  const [taskMode, setTaskMode] = useState(false);
  const [taskSubMode, setTaskSubMode] = useState<"standard" | "browser" | "research">("standard");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string>("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedTaskConnectors, setSelectedTaskConnectors] = useState<string[]>([]);
  const [activeSkills, setActiveSkills] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ fileId: string; name: string }>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showFunnelModal, setShowFunnelModal] = useState(false);
  const [showCreativesModal, setShowCreativesModal] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Mutation para persistir conversa do agente no PostgreSQL
  const saveAgentConversation = trpc.chat.saveAgentConversation.useMutation({
    onSuccess: (data) => {
      if (data.conversationId && !activeConversationId) {
        setActiveConversationId(data.conversationId);
      }
      utils.chat.listUnifiedConversations.invalidate();
      utils.chat.listConversations.invalidate();
    },
  });

  const agentSSE = useAgentSSE({
    onDone: (answer, eventsSnapshot) => {
      // Snapshot events into the agent message so they persist independently
      setAgentMessages((prev) => {
        const updated = [...prev];
        let lastAgent = -1;
        for (let i = updated.length - 1; i >= 0; i--) { if (updated[i].role === "agent") { lastAgent = i; break; } }
        if (lastAgent >= 0) {
          updated[lastAgent] = {
            ...updated[lastAgent],
            isRunning: false,
            content: answer || "",
            events: eventsSnapshot || [],
          };
        }
        // Persist to PostgreSQL
        const lastUserMsg = [...updated].reverse().find((m) => m.role === "user");
        if (lastUserMsg && answer) {
          saveAgentConversation.mutate({
            conversationId: activeConversationId || undefined,
            userMessage: lastUserMsg.content,
            agentResponse: answer,
          });
        }
        return updated;
      });
      setTimeout(() => inputRef.current?.focus(), 200);
    },
    onError: (err) => {
      setAgentMessages((prev) => {
        const updated = [...prev];
        let lastAgent = -1;
        for (let i = updated.length - 1; i >= 0; i--) { if (updated[i].role === "agent") { lastAgent = i; break; } }
        if (lastAgent >= 0) {
          updated[lastAgent] = { ...updated[lastAgent], isRunning: false, events: [...(updated[lastAgent].events || [])] };
        }
        return updated;
      });
    },
  });

  // ─── Voice Integration ─────────────────────────────────────────────────
  const voice = useVoice({
    onTranscription: (text) => {
      setInput((prev) => prev ? `${prev} ${text}` : text);
      setVoiceError(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    },
    onError: (err) => {
      setVoiceError(err);
      setTimeout(() => setVoiceError(null), 4000);
    },
    silenceTimeout: 6000,
  });

  // Unified conversations from all channels
  const { data: unifiedData } = trpc.chat.listUnifiedConversations.useQuery({ filter: channelFilter });
  const unifiedConversations = unifiedData?.conversations || [];
  const sourceCounts = unifiedData?.sources || {};

  // Unified messages — works for any source
  const { data: unifiedMessages, isLoading: loadingUnifiedMessages } = trpc.chat.getUnifiedMessages.useQuery(
    { conversationId: activeConversationId!, source: activeSource },
    { enabled: !!activeConversationId && activeSource !== "web_chat" }
  );

  // Legacy web chat messages (for sending new messages)
  const { data: chatMessages, isLoading: loadingMessages } = trpc.chat.getMessages.useQuery(
    { conversationId: activeConversationId! },
    { enabled: !!activeConversationId && activeSource === "web_chat" }
  );

  // Merge: use unified for external channels, legacy for web_chat
  const displayMessages = activeSource === "web_chat" ? chatMessages : unifiedMessages;
  const isLoadingMsgs = activeSource === "web_chat" ? loadingMessages : loadingUnifiedMessages;

  const lastAssistantMsg = useMemo(() => {
    const msgs = [...(chatMessages || []) as any[]];
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === "assistant") return msgs[i];
    }
    return null;
  }, [chatMessages]);

  const [lastAutoInfo, setLastAutoInfo] = useState<{ model: string; reason: string } | null>(null);
  const [lastToolCalls, setLastToolCalls] = useState<Array<{ tool: string; args: Record<string, unknown>; status: string; preview?: string; error?: string }>>([]);

  const sendMessage = trpc.chat.sendMessage.useMutation({
    onSuccess: (data) => {
      if (data.conversationId && !activeConversationId) {
        setActiveConversationId(data.conversationId);
      }
      if (data.autoMode && data.autoReason) {
        setLastAutoInfo({ model: data.model || "auto", reason: data.autoReason });
      } else {
        setLastAutoInfo(null);
      }
      if (data.toolCalls && data.toolCalls.length > 0) {
        setLastToolCalls(data.toolCalls);
      } else {
        setLastToolCalls([]);
      }
      // Track active skills from keyword matching
      if ((data as any).activeSkills?.length > 0) {
        setActiveSkills((data as any).activeSkills);
      } else {
        setActiveSkills([]);
      }
      setOptimisticMessages([]);
      utils.chat.getMessages.invalidate();
      utils.chat.listConversations.invalidate();
      utils.chat.listUnifiedConversations.invalidate();
    },
    onError: () => {
      setOptimisticMessages([]);
    },
  });

  const deleteConversation = trpc.chat.deleteConversation.useMutation({
    onSuccess: () => {
      setActiveConversationId(null);
      setActiveSource("web_chat");
      utils.chat.listConversations.invalidate();
      utils.chat.listUnifiedConversations.invalidate();
    },
  });

  // ─── Task Mode Mutations & Queries ─────────────────────────────────────
  const { data: brainSkills } = trpc.brain.skills.useQuery(undefined, { enabled: taskMode });
  const { data: manusConnectors } = trpc.brain.getManusConnectors.useQuery(undefined, { enabled: taskMode });
  const { data: tasks } = trpc.brain.listTasks.useQuery(undefined, { refetchInterval: 15000, enabled: taskMode || activeSource === "manus_task" || !!selectedTaskId });
  const uploadFile = trpc.brain.uploadFile.useMutation();
  const sendCommand = trpc.brain.sendCommand.useMutation({
    onSuccess: (newTask) => {
      setInput(""); utils.brain.listTasks.invalidate(); setSelectedTaskId(newTask.id); setActiveSource("manus_task"); setActiveConversationId(newTask.id);
      if ((newTask as any).activeSkills?.length > 0) setActiveSkills((newTask as any).activeSkills);
    },
  });
  const sendBrowserCommand = trpc.brain.sendBrowserCommand.useMutation({
    onSuccess: (newTask) => { setInput(""); utils.brain.listTasks.invalidate(); setSelectedTaskId(newTask.id); setActiveSource("manus_task"); setActiveConversationId(newTask.id); },
  });
  const sendResearchCommand = trpc.brain.sendResearchCommand.useMutation({
    onSuccess: (newTask) => { setInput(""); utils.brain.listTasks.invalidate(); setSelectedTaskId(newTask.id); setActiveSource("manus_task"); setActiveConversationId(newTask.id); },
  });
  const isTaskSubmitting = sendCommand.isPending || sendBrowserCommand.isPending || sendResearchCommand.isPending;

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        const result = await uploadFile.mutateAsync({ fileName: file.name });
        await fetch(result.upload_url, { method: "PUT", body: file, headers: { "Content-Type": file.type || "application/octet-stream" } });
        setUploadedFiles(prev => [...prev, { fileId: result.file_id, name: file.name }]);
      }
    } catch (err) { console.error("Upload error:", err); } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function handleTaskShortcutSuccess(taskId: string) {
    utils.brain.listTasks.invalidate();
    setSelectedTaskId(taskId);
    setActiveSource("manus_task");
    setActiveConversationId(taskId);
  }

  // Auto-detect agent mode from input
  useEffect(() => {
    if (input.trim().length > 10) {
      setAutoAgentDetected(shouldUseAgentMode(input));
    } else {
      setAutoAgentDetected(false);
    }
  }, [input]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, optimisticMessages, agentMessages, agentSSE.events]);


  const handleSend = async () => {
    if (!input.trim()) return;
    const message = input.trim();

    // Task Mode: dispatch to Manus API
    if (taskMode) {
      setInput("");
      const fileIds = uploadedFiles.map(f => f.fileId);
      const connectors = selectedTaskConnectors.length > 0 ? selectedTaskConnectors : undefined;
      const agent = selectedAgent || undefined;
      const skills = selectedSkills.length > 0 ? selectedSkills : undefined;
      if (taskSubMode === "browser") {
        sendBrowserCommand.mutate({ command: message });
      } else if (taskSubMode === "research") {
        sendResearchCommand.mutate({ command: message });
      } else {
        sendCommand.mutate({ command: message, fileIds: fileIds.length > 0 ? fileIds : undefined, connectors, selectedAgent: agent, selectedSkills: skills });
      }
      setUploadedFiles([]);
      return;
    }

    // Decide: agent mode (ReAct with tools) vs chat mode (fast response)
    const useAgent = agentMode || autoAgentDetected || isSandboxActive;

    console.log("[handleSend]", {
      useAgent,
      agentMode,
      autoAgentDetected,
      isSandboxActive,
      enabledConnectors: Array.from(enabledConnectors),
    });

    // Se modo agente está ativo mas sandbox não está rodando, pedir permissão primeiro
    if (useAgent && !isSandboxActive && sandbox.status !== "starting") {
      setSandboxReason(`Tarefa detectada: "${message.slice(0, 80)}${message.length > 80 ? "..." : ""}" \u2014 requer ambiente completo para execu\u00e7\u00e3o.`);
      setShowSandboxPrompt(true);
      // Manter input para que o usuário possa reenviar após aceitar
      return;
    }

    setInput("");

    if (useAgent) {
      // Agent mode: use SSE streaming with ReAct agent
      // Build conversation history (PREVIOUS messages only, NOT current)
      const history = agentMessages
        .filter((m) => m.content)
        .map((m) => ({
          role: m.role === "user" ? "user" : "assistant",
          content: m.content,
        }));

      setAgentMessages((prev) => [
        ...prev,
        { role: "user", content: message, timestamp: Date.now() },
        { role: "agent", content: "", events: [], isRunning: true, timestamp: Date.now() },
      ]);
      agentSSE.execute({
        goal: message,
        connectors: Array.from(enabledConnectors),
        maxSteps: 50,
        history: history.length > 0 ? history : undefined,
      });
    } else {
      // Normal chat mode: fast response via tRPC
      if (sendMessage.isPending) return;
      setOptimisticMessages([{
        id: `opt-${Date.now()}`,
        role: "user",
        content: message,
        createdAt: Date.now(),
      }]);
      sendMessage.mutate({
        message,
        model: selectedModel,
        conversationId: activeConversationId || undefined,
      });
    }
  };

  const handleNewChat = () => {
    setActiveConversationId(null);
    setActiveSource("web_chat");
    setSelectedTaskId(null);
    setOptimisticMessages([]);
    setLastAutoInfo(null);
    setLastToolCalls([]);
    setAgentMessages([]);
    agentSSE.clearEvents();
    setInput("");
  };

  const toggleConnector = (id: string) => {
    setEnabledConnectors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Agent conversation is active when there are agent messages
  const isAgentConversation = agentMessages.length > 0;
  const isBusy = agentSSE.isRunning || isTaskSubmitting;
  const selectedTask = tasks?.find((t) => t.id === selectedTaskId);

  const isSandboxActive = sandbox.status === "running" || sandbox.status === "starting";

  // Auto-add sandbox connector when sandbox is active
  React.useEffect(() => {
    if (isSandboxActive && !enabledConnectors.has("sandbox")) {
      setEnabledConnectors((prev) => new Set([...prev, "sandbox"]));
    }
  }, [isSandboxActive]);

  return (
    <>
    <div className={`flex h-[calc(100vh-11rem)] gap-3 ${isSandboxActive ? "sandbox-active" : ""}`}>
      {/* Sidebar — Unified Conversations from all channels */}
      <div className="w-64 bg-card border border-border rounded-xl flex flex-col overflow-hidden shrink-0 hidden lg:flex">
        <div className="p-3 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-foreground">Conversas</span>
            <button onClick={handleNewChat} className="p-1 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" title="Nova conversa">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          {/* Channel filter pills */}
          <div className="flex flex-wrap gap-1">
            {(["all", "web_chat", "telegram", "whatsapp", "manus_task"] as const).map((f) => {
              const labels: Record<string, string> = { all: "Todos", web_chat: "Chat", telegram: "Telegram", whatsapp: "WhatsApp", manus_task: "Tasks" };
              const colors: Record<string, string> = { all: "text-foreground bg-secondary", web_chat: "text-purple-400 bg-purple-500/10", telegram: "text-blue-400 bg-blue-500/10", whatsapp: "text-green-400 bg-green-500/10", manus_task: "text-amber-400 bg-amber-500/10" };
              const count = f === "all" ? unifiedConversations.length : (sourceCounts[f] || 0);
              return (
                <button
                  key={f}
                  onClick={() => setChannelFilter(f)}
                  className={`px-2 py-0.5 rounded-full text-[9px] font-medium transition-all border ${
                    channelFilter === f
                      ? `${colors[f]} border-current/30`
                      : "text-muted-foreground/60 bg-transparent border-transparent hover:bg-secondary/50"
                  }`}
                >
                  {labels[f]}{count > 0 ? ` (${count})` : ""}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {unifiedConversations.map((conv: any) => {
            const sourceIcons: Record<string, any> = { web_chat: MessageCircle, telegram: Send, whatsapp: Smartphone, manus_task: Cpu };
            const sourceColors: Record<string, string> = { web_chat: "text-purple-400", telegram: "text-blue-400", whatsapp: "text-green-400", manus_task: "text-amber-400" };
            const SourceIcon = sourceIcons[conv.source] || MessageCircle;
            const isActive = activeConversationId === conv.id;
            return (
              <button
                key={conv.id}
                onClick={() => {
                  setActiveConversationId(conv.id);
                  setActiveSource(conv.source);
                  setAgentMessages([]);
                  agentSSE.clearEvents();
                  // If it's a manus_task, set the selectedTaskId to show TaskDetail
                  if (conv.source === "manus_task") {
                    setSelectedTaskId(conv.id.replace(/^task:/, ""));
                  } else {
                    setSelectedTaskId(null);
                  }
                }}
                className={`w-full text-left p-2.5 rounded-lg text-xs transition-all group ${
                  isActive ? "bg-primary/10 text-foreground border border-primary/20" : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
                }`}
              >
                <div className="flex items-center gap-2">
                  <SourceIcon className={`w-3 h-3 shrink-0 ${sourceColors[conv.source] || "text-muted-foreground"}`} />
                  <p className="truncate flex-1 text-[11px]">{conv.title || "Nova conversa"}</p>
                  {conv.source === "web_chat" && (
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteConversation.mutate({ conversationId: conv.id }); }}
                      className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-destructive transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                  {conv.status && (
                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium ${
                      conv.status === "completed" ? "bg-green-500/10 text-green-400" :
                      conv.status === "running" ? "bg-amber-500/10 text-amber-400" :
                      "bg-blue-500/10 text-blue-400"
                    }`}>{conv.status === "completed" ? "OK" : conv.status === "running" ? "..." : conv.status}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-[9px] text-muted-foreground/60 truncate flex-1">{conv.lastMessage || ""}</p>
                  <span className="text-[8px] text-muted-foreground/40 shrink-0">
                    {conv.lastMessageAt && Number(conv.lastMessageAt) > 0 ? (() => { const d = new Date(Number(conv.lastMessageAt)); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("pt-BR"); })() : ""}
                  </span>
                </div>
                {conv.messageCount > 1 && (
                  <span className="text-[8px] text-muted-foreground/40 mt-0.5">{conv.messageCount} msgs</span>
                )}
              </button>
            );
          })}
          {unifiedConversations.length === 0 && (
            <p className="text-[10px] text-muted-foreground/50 text-center py-4">Nenhuma conversa</p>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 bg-card border border-border rounded-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between bg-card/80 backdrop-blur-sm shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-orange-600 rounded-full flex items-center justify-center shadow-md shadow-primary/20">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground text-sm leading-tight">Megan</h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                {isBusy ? (
                  <><span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse" /><span className="text-[10px] text-yellow-500">{agentSSE.isRunning ? "Agente executando..." : "Pensando..."}</span></>
                ) : (
                  <><span className="w-1.5 h-1.5 bg-success rounded-full" /><span className="text-[10px] text-success">Online</span></>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ModelSelector value={selectedModel} onChange={onModelChange} />
            {/* Connectors Toggle (sempre ativo, mostra/esconde painel de conectores) */}
            <button
              onClick={() => setShowConnectorPanel(!showConnectorPanel)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                showConnectorPanel
                  ? "bg-violet-500/10 text-violet-400 border-violet-500/30"
                  : "bg-secondary/50 text-muted-foreground border-border hover:border-violet-500/20"
              }`}
              title="Conectores: GitHub, Railway, Database, Web Search, Sandbox"
            >
              <Plug className="w-3.5 h-3.5" />
              Conectores
            </button>
            {/* Modo Task (API Manus) */}
            <button
              onClick={() => setTaskMode(!taskMode)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                taskMode
                  ? "bg-blue-500/10 text-blue-400 border-blue-500/30"
                  : "bg-secondary/50 text-muted-foreground border-border hover:border-blue-500/20"
              }`}
              title="Modo Task: cria tarefas com agentes, skills e conectores Manus"
            >
              <BrainCircuit className="w-3.5 h-3.5" />
              Task
            </button>
            {/* Modo Agente Unificado (Agente + Sandbox + Task) */}
            <button
              onClick={() => {
                if (isSandboxActive) {
                  sandbox.stopSandbox();
                } else if (sandbox.status === "starting") {
                  // Já está iniciando, não fazer nada
                } else {
                  setSandboxReason("Ativação manual do Sandbox para tarefas complexas.");
                  setShowSandboxPrompt(true);
                }
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                isSandboxActive
                  ? "bg-green-500/10 text-green-400 border-green-500/30"
                  : sandbox.status === "starting"
                  ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/30 animate-pulse"
                  : sandbox.status === "error"
                  ? "bg-red-500/10 text-red-400 border-red-500/30"
                  : "bg-secondary/50 text-muted-foreground border-border hover:border-green-500/20"
              }`}
              title={sandbox.error ? `Erro: ${sandbox.error}` : "Sandbox: ambiente Linux isolado com terminal, browser e ferramentas para tarefas complexas"}
            >
              {sandbox.status === "starting" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : isSandboxActive ? (
                <Monitor className="w-3.5 h-3.5" />
              ) : (
                <Terminal className="w-3.5 h-3.5" />
              )}
              {sandbox.status === "starting" ? "Iniciando..." : sandbox.status === "error" ? "Erro" : isSandboxActive ? "Sandbox Ativo" : "Sandbox"}
            </button>
            {/* Sandbox error toast */}
            {sandbox.error && (
              <span className="text-[10px] text-red-400 max-w-[200px] truncate" title={sandbox.error}>
                {sandbox.error}
              </span>
            )}

            <button onClick={handleNewChat} className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" title="Nova conversa">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Connector Panel (collapsible) */}
        {showConnectorPanel && (
          <div className="px-4 py-2.5 border-b border-border bg-secondary/20">
            <div className="flex items-center gap-2 mb-1.5">
              <Plug className="w-3 h-3 text-primary" />
              <span className="text-[10px] font-medium text-foreground">Conectores</span>
              <span className="text-[9px] text-muted-foreground ml-auto">Ferramentas que o agente pode usar</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_CONNECTORS.map((conn) => {
                const isEnabled = enabledConnectors.has(conn.id);
                return (
                  <button
                    key={conn.id}
                    onClick={() => toggleConnector(conn.id)}
                    disabled={agentSSE.isRunning}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all border ${
                      isEnabled
                        ? "bg-primary/10 text-primary border-primary/30"
                        : "bg-card text-muted-foreground border-border opacity-60"
                    } ${agentSSE.isRunning ? "cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <conn.icon className="w-3 h-3" />
                    {conn.label}
                    {isEnabled && <CheckCircle2 className="w-2.5 h-2.5" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Messages Area — or TaskDetail when a task is selected */}
        {selectedTask ? (
          <div className="flex-1 overflow-y-auto">
            <TaskDetail task={selectedTask} onBack={() => { setSelectedTaskId(null); setActiveConversationId(null); setActiveSource("web_chat"); }} />
          </div>
        ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Empty state */}
          {!activeConversationId && !isAgentConversation && optimisticMessages.length === 0 && !chatMessages?.length && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-14 h-14 bg-gradient-to-br from-primary/20 to-orange-600/20 rounded-full flex items-center justify-center mb-4 shadow-xl shadow-primary/10">
                <Bot className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-1">Olá! Sou a Megan</h3>
              <p className="text-xs text-muted-foreground max-w-md leading-relaxed mb-4">
                Sou sua assistente inteligente com acesso a conectores, sandbox e ferramentas.
                Posso conversar, executar ações ou ativar o <strong>Modo Task</strong> para tarefas complexas.
              </p>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-full text-[10px] text-violet-400 mb-4">
                <Target className="w-3 h-3" />
                Agente inteligente ativo — {enabledConnectors.size} conectores disponíveis
              </div>
              {taskMode && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full text-[10px] text-blue-400 mb-4">
                  <BrainCircuit className="w-3 h-3" />
                  Modo Task ativo — tarefas complexas via Manus API
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 max-w-lg w-full">
                {[
                  { icon: MessageCircle, text: "Explique como funciona o sistema YOS" },
                  { icon: GitBranch, text: "Verifique o status de todos os serviços no Railway" },
                  { icon: Globe, text: "Pesquise os top nichos de arbitragem com melhor ROI" },
                  { icon: BarChart3, text: "Analise o nicho 'solar energy' e recomende estratégia" },
                ].map(({ icon: Icon, text }) => (
                  <button
                    key={text}
                    onClick={() => setInput(text)}
                    className="flex items-start gap-2 px-3 py-2.5 bg-secondary hover:bg-secondary/80 border border-border hover:border-primary/30 rounded-xl text-[11px] text-muted-foreground hover:text-foreground transition-all text-left"
                  >
                    <Icon className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                    <span>{text}</span>
                  </button>
                ))}
              </div>
              {/* Task Shortcuts */}
              <div className="flex gap-2 mt-4">
                <button onClick={() => setShowFunnelModal(true)} className="flex items-center gap-1.5 px-3 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/20 rounded-xl text-[11px] text-primary font-medium transition-all">
                  <Layers className="w-3.5 h-3.5" /> Criar Funil
                </button>
                <button onClick={() => setShowCreativesModal(true)} className="flex items-center gap-1.5 px-3 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/20 rounded-xl text-[11px] text-primary font-medium transition-all">
                  <Palette className="w-3.5 h-3.5" /> Criar Criativos
                </button>
              </div>
            </div>
          )}

          {/* Channel badge for external conversations */}
          {activeConversationId && activeSource !== "web_chat" && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary/50 border border-border rounded-lg text-[10px] text-muted-foreground mb-2">
              {activeSource === "telegram" && <><Send className="w-3 h-3 text-blue-400" /><span>Conversa via <strong className="text-blue-400">Telegram</strong></span></>}
              {activeSource === "whatsapp" && <><Smartphone className="w-3 h-3 text-green-400" /><span>Conversa via <strong className="text-green-400">WhatsApp</strong></span></>}
              {activeSource === "manus_task" && <><Cpu className="w-3 h-3 text-amber-400" /><span>Execu\u00e7\u00e3o via <strong className="text-amber-400">Manus Task</strong></span></>}
              <span className="ml-auto text-[9px] text-muted-foreground/50">Somente leitura</span>
            </div>
          )}

          {/* Messages (unified from any channel) */}
          {isLoadingMsgs && activeConversationId && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-muted shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {(displayMessages as any[])?.map((msg: any, i: number) => (
            <MessageBubble key={msg.id || i} msg={msg} isLast={!isAgentConversation && i === ((displayMessages as any[])?.length || 0) - 1} />
          ))}
          {optimisticMessages.map((msg) => (
            <MessageBubble key={msg.id} msg={{ role: msg.role, content: msg.content, timestamp: msg.createdAt }} isLast={!isAgentConversation} />
          ))}
          {sendMessage.isPending && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center shrink-0">
                <Bot className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="flex items-center gap-1.5 px-4 py-3 bg-card border border-border rounded-2xl rounded-tl-md">
                <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}

          {/* Active Skills indicator (shown in all modes) */}
          {activeSkills.length > 0 && (agentMessages.length > 0 || selectedSkills.length > 0) && (
            <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-500/5 to-orange-500/5 border border-amber-500/15 rounded-lg">
              <div className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-[11px] font-semibold text-amber-400">Skills Ativas</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {activeSkills.filter(s => s !== "ymonetize-brain-os").map((skill) => (
                  <span key={skill} className="px-2 py-0.5 bg-amber-500/10 text-amber-300 border border-amber-500/20 rounded-full text-[9px] font-mono flex items-center gap-1">
                    <Wrench className="w-2.5 h-2.5" />
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Agent messages (inline after chat messages) */}
          {agentMessages.map((msg, i) => {
            // Determine if this is the LAST agent message (the one currently running)
            const isLastAgentMsg = msg.role === "agent" && i === agentMessages.length - 1;
            // Use live events for the running message, snapshotted events for completed ones
            const msgEvents = (msg.isRunning && isLastAgentMsg) ? agentSSE.events : (msg.events || []);
            const msgRunning = (msg.isRunning && isLastAgentMsg) ? agentSSE.isRunning : false;
            const msgStreaming = (msg.isRunning && isLastAgentMsg) ? agentSSE.streamingText : undefined;

            return (
              <div key={`agent-${i}`} className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                  msg.role === "user" ? "bg-primary/20" : "bg-gradient-to-br from-violet-600 to-primary"
                }`}>
                  {msg.role === "user" ? <User className="w-3.5 h-3.5 text-primary" /> : <Target className="w-3.5 h-3.5 text-white" />}
                </div>
                <div className={`flex-1 min-w-0 max-w-[90%] ${msg.role === "user" ? "text-right" : ""}`}>
                  {msg.role === "user" ? (
                    <div className="inline-block text-left rounded-2xl px-4 py-3 bg-primary text-primary-foreground rounded-tr-md">
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <AgentStepInline events={msgEvents} isRunning={msgRunning} streamingText={msgStreaming} fallbackContent={!msg.isRunning ? msg.content : undefined} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Auto-info and tool calls (normal chat) */}
          {lastAutoInfo && !sendMessage.isPending && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/10 rounded-lg text-[10px] text-muted-foreground">
              <Sparkles className="w-3 h-3 text-primary" />
              <span>Auto Mode selecionou <strong className="text-foreground">{lastAutoInfo.model}</strong> — {lastAutoInfo.reason}</span>
            </div>
          )}
          {activeSkills.length > 0 && !sendMessage.isPending && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/5 border border-amber-500/15 rounded-lg text-[10px] text-muted-foreground">
              <Zap className="w-3 h-3 text-amber-400" />
              <span className="font-medium text-amber-400">Skills Ativas:</span>
              <div className="flex flex-wrap gap-1">
                {activeSkills.filter(s => s !== "ymonetize-brain-os").map((skill) => (
                  <span key={skill} className="px-1.5 py-0.5 bg-amber-500/10 text-amber-300 border border-amber-500/20 rounded text-[9px] font-mono">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
          {lastToolCalls.length > 0 && !sendMessage.isPending && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 px-3 py-1 text-[10px] text-muted-foreground">
                <Wrench className="w-3 h-3 text-primary" />
                <span className="font-medium">Brain usou {lastToolCalls.length} ferramenta{lastToolCalls.length > 1 ? "s" : ""}</span>
              </div>
              {lastToolCalls.map((tc, i) => (
                <div key={i} className="flex items-start gap-2 px-3 py-1.5 bg-secondary/50 border border-border/50 rounded-lg text-[10px]">
                  <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    tc.status === "success" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
                  }`}>
                    {tc.status === "success" ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-mono font-medium text-foreground">{tc.tool}</span>
                    {tc.preview && <p className="text-muted-foreground mt-0.5 truncate">{tc.preview.slice(0, 120)}</p>}
                    {tc.error && <p className="text-destructive mt-0.5">{tc.error}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {sendMessage.error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-destructive/5 border border-destructive/20 rounded-lg text-xs text-destructive">
              <AlertCircle className="w-3.5 h-3.5" />
              {sendMessage.error.message}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
        )}

        {/* Voice Status */}
        <VoiceStatusBar state={voice.state} volume={voice.volume} />
        {voiceError && (
          <div className="mx-3 mt-1 px-3 py-1.5 bg-destructive/10 border border-destructive/20 rounded-lg text-[11px] text-destructive">
            {voiceError}
          </div>
        )}

        {/* Input Area — hidden for external channel conversations (read-only) or when viewing a task */}
        <div className={`p-3 border-t border-border shrink-0 ${selectedTask ? "hidden" : ""} ${activeSource !== "web_chat" && !taskMode ? "opacity-50 pointer-events-none" : ""}`}>
          {/* Auto-agent detection hint */}
          {autoAgentDetected && !agentMode && !taskMode && (
            <div className="flex items-center gap-2 px-3 py-1.5 mb-2 bg-violet-500/10 border border-violet-500/20 rounded-lg text-[10px] text-violet-400 animate-fade-in">
              <Target className="w-3 h-3" />
              <span>Tarefa complexa detectada — será executada no <strong>Modo Agente</strong> automaticamente</span>
              <button onClick={() => setAutoAgentDetected(false)} className="ml-auto text-violet-300 hover:text-violet-200">
                <AlertCircle className="w-3 h-3" />
              </button>
            </div>
          )}
          {/* Task Mode: Sub-mode selector */}
          {taskMode && (
            <div className="mb-2 space-y-2">
              <div className="flex items-center gap-1.5">
                {(["standard", "browser", "research"] as const).map((mode) => {
                  const modeIcons = { standard: Terminal, browser: Globe, research: BarChart3 };
                  const modeLabels = { standard: "Padrão", browser: "Browser", research: "Pesquisa" };
                  const MIcon = modeIcons[mode];
                  return (
                    <button key={mode} onClick={() => setTaskSubMode(mode)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all border ${
                      taskSubMode === mode ? "bg-blue-500/10 text-blue-400 border-blue-500/30" : "bg-secondary/50 text-muted-foreground border-border hover:border-blue-500/20"
                    }`}>
                      <MIcon className="w-3 h-3" />{modeLabels[mode]}
                    </button>
                  );
                })}
                <div className="ml-auto flex items-center gap-1.5">
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json,.xlsx" />
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-primary/20 transition-all">
                    {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />} Anexar
                  </button>
                  <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border transition-all ${
                    showAdvanced ? "bg-primary/10 text-primary border-primary/30" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/20"
                  }`}>
                    <Settings2 className="w-3 h-3" /> Avançado
                  </button>
                </div>
              </div>
              {taskSubMode !== "standard" && (
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] ${
                  taskSubMode === "browser" ? "bg-blue-500/10 border border-blue-500/20 text-blue-400" : "bg-violet-500/10 border border-violet-500/20 text-violet-400"
                }`}>
                  {taskSubMode === "browser" ? <><Globe className="w-3 h-3" /> Browser: navegador web para automação, scraping e interações.</> : <><BarChart3 className="w-3 h-3" /> Pesquisa: Wide Research com Perplexity + Firecrawl + Browser.</>}
                </div>
              )}
              {uploadedFiles.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {uploadedFiles.map((f) => (
                    <span key={f.fileId} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                      <FileText className="w-2.5 h-2.5" />{f.name}
                      <button type="button" onClick={() => setUploadedFiles(prev => prev.filter(x => x.fileId !== f.fileId))} className="hover:text-destructive ml-0.5"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  ))}
                </div>
              )}
              {/* Advanced Panel: Agent, Skills, Connectors */}
              {showAdvanced && (
                <div className="p-2.5 bg-background/50 border border-border rounded-xl space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block">Agente YOS</label>
                      <select value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)} className="w-full px-2.5 py-1.5 bg-background border border-input rounded-lg text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50">
                        <option value="">Auto (Megan decide)</option>
                        <option value="megan">Megan (Orquestradora)</option>
                        <option value="publish_manager">Publish Manager (Publicação)</option>
                        <option value="strategist">Strategist (Estratégias)</option>
                        <option value="developer">Developer Agent</option>
                        <option value="researcher">Research Agent</option>
                        <option value="content">Content Agent</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block">Skills</label>
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(brainSkills) ? brainSkills : []).map((skill: any) => (
                          <button key={skill.name} type="button" onClick={() => setSelectedSkills(prev => prev.includes(skill.name) ? prev.filter(s => s !== skill.name) : [...prev, skill.name])} className={`text-[10px] px-2 py-0.5 rounded-full border transition-all ${
                            selectedSkills.includes(skill.name) ? "bg-violet-500/10 text-violet-400 border-violet-500/20" : "border-border text-muted-foreground hover:border-violet-500/20"
                          }`}>
                            <Zap className="w-2.5 h-2.5 inline mr-0.5" />{skill.name}
                          </button>
                        ))}
                        {(!brainSkills || brainSkills.length === 0) && <span className="text-[10px] text-muted-foreground/50">Carregando...</span>}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block">Conectores Manus</label>
                      <div className="flex flex-wrap gap-1">
                        {(manusConnectors?.connectors || []).map((conn: any) => (
                          <button key={conn.id} type="button" onClick={() => setSelectedTaskConnectors(prev => prev.includes(conn.id) ? prev.filter(c => c !== conn.id) : [...prev, conn.id])} className={`text-[10px] px-2 py-0.5 rounded-full border transition-all ${
                            selectedTaskConnectors.includes(conn.id) ? "bg-success/10 text-success border-success/20" : "border-border text-muted-foreground hover:border-success/20"
                          }`}>
                            <Plug className="w-2.5 h-2.5 inline mr-0.5" />{conn.name}
                          </button>
                        ))}
                        {(!manusConnectors?.connectors || manusConnectors.connectors.length === 0) && <span className="text-[10px] text-muted-foreground/50">Carregando...</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Uploaded files preview (fora do task mode) */}
          {!taskMode && uploadedFiles.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1.5">
              {uploadedFiles.map((f) => (
                <span key={f.fileId} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  <FileText className="w-2.5 h-2.5" />{f.name}
                  <button type="button" onClick={() => setUploadedFiles(prev => prev.filter(x => x.fileId !== f.fileId))} className="hover:text-destructive ml-0.5"><X className="w-2.5 h-2.5" /></button>
                </span>
              ))}
            </div>
          )}

          <div className="flex items-end gap-2 bg-secondary/70 border border-border rounded-xl px-3 py-2 focus-within:border-primary/40 transition-colors">
            <VoiceButton state={voice.state} volume={voice.volume} onClick={voice.toggle} size="sm" />
            {/* Upload button — sempre visível */}
            {!taskMode && (
              <>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json,.xlsx" />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || agentSSE.isRunning}
                  className="p-2 text-muted-foreground hover:text-primary rounded-lg hover:bg-primary/10 transition-all disabled:opacity-40"
                  title="Anexar arquivo"
                >
                  {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                </button>
              </>
            )}
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                // Auto-resize textarea
                const el = e.target;
                el.style.height = 'auto';
                el.style.height = Math.min(el.scrollHeight, 200) + 'px';
              }}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={
                voice.isRecording ? "Escutando... fale agora" :
                voice.isProcessing ? "Transcrevendo..." :
                taskMode ? (taskSubMode === "browser" ? "Descreva a tarefa web..." : taskSubMode === "research" ? "Descreva o que pesquisar..." : "Descreva o que o Brain deve fazer...") :
                agentMode ? "Descreva uma tarefa para o agente executar..." :
                "Pergunte ou peça algo à Megan..."
              }
              rows={1}
              className="flex-1 bg-transparent text-foreground text-sm resize-none outline-none py-1.5 placeholder:text-muted-foreground overflow-y-auto"
              style={{ minHeight: "32px", maxHeight: "200px" }}
              disabled={agentSSE.isRunning}
            />
            {lastAssistantMsg && !voice.isSpeaking && !isAgentConversation && !taskMode && (
              <button onClick={() => voice.speak(lastAssistantMsg.content)} className="p-2 text-muted-foreground hover:text-primary rounded-lg hover:bg-primary/10 transition-all" title="Ouvir resposta">
                <Volume2 className="w-4 h-4" />
              </button>
            )}
            {voice.isSpeaking && (
              <button onClick={voice.stopSpeaking} className="p-2 text-primary bg-primary/10 rounded-lg animate-pulse" title="Parar áudio">
                <Volume2 className="w-4 h-4" />
              </button>
            )}
            {agentSSE.isRunning ? (
              <button onClick={agentSSE.stop} className="p-2 bg-destructive text-destructive-foreground rounded-lg hover:opacity-90 active:scale-95 transition-all shadow-md" title="Parar agente">
                <Square className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!input.trim() || sendMessage.isPending || isTaskSubmitting}
                className="p-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 active:scale-95 transition-all disabled:opacity-40 shadow-md shadow-primary/20"
              >
                {(sendMessage.isPending || isTaskSubmitting) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            )}
          </div>
          {/* Task error messages */}
          {(sendCommand.error || sendBrowserCommand.error || sendResearchCommand.error) && (
            <p className="text-xs text-destructive mt-1.5 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" />{(sendCommand.error || sendBrowserCommand.error || sendResearchCommand.error)?.message}</p>
          )}
          <div className="flex items-center justify-between mt-1.5 px-1">
            <div className="flex items-center gap-3">
              <button
                onClick={() => voice.wakeWordActive ? voice.stopWakeWordListener() : voice.startWakeWordListener()}
                className={`text-[9px] flex items-center gap-1 transition-colors ${
                  voice.wakeWordActive ? "text-yellow-500" : "text-muted-foreground/50 hover:text-muted-foreground"
                }`}
              >
                <Radio className="w-2.5 h-2.5" />
                {voice.wakeWordActive ? '"Olá Megan" ativo' : 'Ativar "Olá Megan"'}
              </button>
              {(agentMode || autoAgentDetected) && (
                <span className="text-[9px] text-violet-400/60 flex items-center gap-1">
                  <Plug className="w-2.5 h-2.5" />
                  {Array.from(enabledConnectors).join(", ")}
                </span>
              )}
              {taskMode && (
                <span className="text-[9px] text-blue-400/60 flex items-center gap-1">
                  <BrainCircuit className="w-2.5 h-2.5" />
                  Modo Task: {taskSubMode === "browser" ? "Browser" : taskSubMode === "research" ? "Pesquisa" : "Padrão"}
                </span>
              )}
            </div>
            <p className="text-[9px] text-muted-foreground/50">
              Enter para enviar · Shift+Enter para nova linha
            </p>
          </div>
        </div>
      </div>

      {/* Sandbox Panel — aparece como split view quando ativo */}
      {isSandboxActive && (
        <div className="w-1/2 shrink-0 h-full">
          <SandboxPanel
            vncUrl={sandbox.vncUrl}
            wsUrl={sandbox.wsUrl}
            sandboxUrl={sandbox.sandboxUrl}
            activeView={sandbox.activeView}
            onViewChange={sandbox.setActiveView}
            onStop={sandbox.stopSandbox}
            isLoading={sandbox.status === "starting"}
            sessionId={sandbox.sessionId ?? undefined}
            agentEvents={agentSSE.events}
            isAgentRunning={agentSSE.isRunning}
          />
        </div>
      )}
    </div>

    {/* Agent Mode Prompt — exibido quando a Megan sugere ativar o sandbox */}
    {showSandboxPrompt && !isSandboxActive && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <AgentModePrompt
          onAccept={() => {
            setShowSandboxPrompt(false);
            setAgentMode(true);
            sandbox.startSandbox();
            // Após aceitar, o usuário pode reenviar a mensagem (input mantido)
          }}
          onDecline={() => {
            setShowSandboxPrompt(false);
            // Se recusou, enviar como chat normal (sem agente)
            if (input.trim()) {
              setAgentMode(false);
            }
          }}
          isLoading={sandbox.status === "starting"}
          reason={sandboxReason}
        />
      </div>
    )}

    {/* Modals */}
    <CreateFunnelModal open={showFunnelModal} onClose={() => setShowFunnelModal(false)} onSuccess={handleTaskShortcutSuccess} />
    <CreateCreativesModal open={showCreativesModal} onClose={() => setShowCreativesModal(false)} onSuccess={handleTaskShortcutSuccess} />
    </>
  );
}

// ─── Conversation Message Bubble ─────────────────────────────────────────────

interface ConversationMessage {
  role: "user" | "assistant";
  content: string;
  attachments?: Array<{ file_name: string; url: string; size_bytes: number; mime_type?: string }>;
  timestamp?: number;
}

function MessageBubble({ msg, isLast }: { msg: ConversationMessage; isLast: boolean }) {
  const isUser = msg.role === "user";

  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""} ${isLast ? "animate-fade-in" : ""}`}>
      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-1 ${
        isUser ? "bg-primary/20" : "bg-gradient-to-br from-primary to-orange-600 shadow-sm shadow-primary/20"
      }`}>
        {isUser ? <User className="w-3.5 h-3.5 text-primary" /> : <Bot className="w-3.5 h-3.5 text-white" />}
      </div>
      <div className={`flex-1 min-w-0 max-w-[85%] ${isUser ? "text-right" : ""}`}>
        <div className={`inline-block text-left rounded-2xl overflow-hidden ${
          isUser
            ? "bg-primary text-primary-foreground rounded-tr-md px-4 py-2.5"
            : "bg-card border border-border rounded-tl-md px-5 py-4"
        }`} style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap leading-relaxed break-words">{msg.content}</p>
          ) : (
            <div className="text-sm leading-relaxed break-words prose-pre:overflow-x-auto prose-pre:max-w-full">
              <RichMarkdown content={msg.content} />
            </div>
          )}
        </div>
        {msg.attachments && msg.attachments.length > 0 && (
          <div className={`mt-2 ${isUser ? "flex justify-end" : ""}`}>
            <TaskAttachments attachments={msg.attachments.map((a) => ({ fileName: a.file_name, url: a.url, sizeBytes: a.size_bytes }))} />
          </div>
        )}
        {msg.timestamp && Number(msg.timestamp) > 0 && !isNaN(new Date(Number(msg.timestamp)).getTime()) && (
          <p className={`text-[10px] text-muted-foreground/60 mt-1 px-1 ${isUser ? "text-right" : ""}`}>
            {new Date(Number(msg.timestamp)).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Timeline Event (compact) ────────────────────────────────────────────────

function TimelineEvent({ event, isLast }: { event: TaskEvent; isLast: boolean }) {
  const isCreated = event.type === "task_created";
  const isStopped = event.type === "task_stopped";
  const isCompleted = isStopped && event.stopReason === "finish";

  let dotColor = "bg-muted-foreground";
  if (isCreated) dotColor = "bg-primary";
  if (event.type === "task_progress") dotColor = "bg-primary";
  if (isCompleted) dotColor = "bg-success";
  if (isStopped && event.stopReason === "ask") dotColor = "bg-yellow-500";
  if (event.progressType === "user_reply") return null;

  return (
    <div className={`flex items-start gap-2.5 py-1 ${isLast ? "animate-fade-in" : ""}`}>
      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${dotColor} ${isLast && !isCompleted ? "animate-pulse" : ""}`} />
      <p className="text-xs text-muted-foreground line-clamp-2 flex-1">{event.message || "Processando..."}</p>
      <span className="text-[10px] text-muted-foreground/60 shrink-0">
        {new Date(event.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
      </span>
    </div>
  );
}

// ─── Task Detail (Chat View) ─────────────────────────────────────────────────

function TaskDetail({
  task,
  onBack,
}: {
  task: { id: string; prompt: string; title?: string | null; status: string; agentProfile: string; output: any; creditUsage: number | null; taskUrl?: string | null; createdAt: number; updatedAt: number };
  onBack: () => void;
}) {
  const [replyText, setReplyText] = useState("");
  const [showTimeline, setShowTimeline] = useState(true);
  const [showReplyAdvanced, setShowReplyAdvanced] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(task.title || "");
  const [replyAgent, setReplyAgent] = useState<string>("");
  const [replySkills, setReplySkills] = useState<string[]>([]);
  const [replyConnectors, setReplyConnectors] = useState<string[]>([]);
  const [replyFiles, setReplyFiles] = useState<Array<{ fileId: string; name: string; size: number; type: string; previewUrl?: string; uploading?: boolean; error?: boolean }>>([]);
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);
  const [isUploadingReply, setIsUploadingReply] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const timelineEndRef = useRef<HTMLDivElement>(null);
  const replyFileInputRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();
  const isActive = task.status === "pending" || task.status === "running";

  // SSE for real-time events + faster polling (3s when active)
  const { events: sseEvents, isConnected } = useTaskSSE({ taskId: task.id, enabled: isActive });
  const { data: dbEvents } = trpc.brain.taskEvents.useQuery({ taskId: task.id }, { refetchInterval: isActive && !isConnected ? 3000 : false });
  // Refetch a cada 3s quando ativa, e uma vez extra quando acabou de completar (para pegar output final)
  const [justCompleted, setJustCompleted] = useState(false);
  const { data: liveTask } = trpc.brain.pollTask.useQuery({ taskId: task.id }, { refetchInterval: isActive ? 3000 : justCompleted ? 2000 : false });

  // Detect transition to completed and trigger extra fetches to get final output
  useEffect(() => {
    if (liveTask && (liveTask.status === "completed" || liveTask.status === "failed") && !justCompleted) {
      setJustCompleted(true);
      const timer = setTimeout(() => setJustCompleted(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [liveTask?.status]);

  const currentTask = liveTask || task;
  const taskUrl = currentTask.taskUrl || task.taskUrl;
  const StatusIcon = statusIcons[currentTask.status] || Clock;

  const replyMutation = trpc.brain.replyTask.useMutation({
    onSuccess: () => {
      setReplyText("");
      setReplyFiles([]);
      setReplyAgent("");
      setReplySkills([]);
      setReplyConnectors([]);
      setShowReplyAdvanced(false);
      utils.brain.pollTask.invalidate({ taskId: task.id });
      utils.brain.taskEvents.invalidate({ taskId: task.id });
    },
  });

  const uploadFileMutation = trpc.brain.uploadFile.useMutation();
  const { data: manusConnectors } = trpc.brain.getManusConnectors.useQuery();

  const renameMutation = trpc.brain.renameTask.useMutation({
    onSuccess: (data) => {
      setIsRenaming(false);
      setRenameValue(data.title);
      utils.brain.listTasks.invalidate();
      utils.brain.pollTask.invalidate({ taskId: task.id });
    },
  });

  async function handleReplyFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      const previewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined;
      const tempId = `uploading-${Date.now()}-${Math.random()}`;
      setReplyFiles(prev => [...prev, { fileId: tempId, name: file.name, size: file.size, type: file.type, previewUrl, uploading: true }]);
      try {
        const result = await uploadFileMutation.mutateAsync({ fileName: file.name });
        await fetch(result.upload_url, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type || "application/octet-stream" },
        });
        setReplyFiles(prev => prev.map(f => f.fileId === tempId ? { ...f, fileId: result.file_id, uploading: false } : f));
      } catch (err) {
        console.error("Upload error:", err);
        setReplyFiles(prev => prev.map(f => f.fileId === tempId ? { ...f, uploading: false, error: true } : f));
      }
    }
    if (replyFileInputRef.current) replyFileInputRef.current.value = "";
  }

  function removeReplyFile(fileId: string) {
    setReplyFiles(prev => {
      const file = prev.find(f => f.fileId === fileId);
      if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
      return prev.filter(f => f.fileId !== fileId);
    });
  }

  function handleReplySubmit() {
    if (!replyText.trim() && replyFiles.length === 0) return;
    replyMutation.mutate({
      taskId: task.id,
      message: replyText.trim() || "[Arquivos anexados]",
      fileIds: replyFiles.length > 0 ? replyFiles.map(f => f.fileId) : undefined,
      connectors: replyConnectors.length > 0 ? replyConnectors : undefined,
      selectedAgent: replyAgent || undefined,
      selectedSkills: replySkills.length > 0 ? replySkills : undefined,
    });
  }

  // Detect if Manus is asking for approval/input
  const askEvent = useMemo(() => {
    // Check SSE events first (most recent)
    for (let i = sseEvents.length - 1; i >= 0; i--) {
      if (sseEvents[i].type === "task_stopped" && sseEvents[i].stopReason === "ask") return sseEvents[i];
    }
    // Then check DB events
    if (dbEvents && dbEvents.length > 0) {
      for (let i = dbEvents.length - 1; i >= 0; i--) {
        if (dbEvents[i].eventType === "task_stopped" && dbEvents[i].stopReason === "ask") return dbEvents[i];
      }
    }
    return null;
  }, [sseEvents, dbEvents]);

  const isWaitingForReply = !!askEvent;

  // Detect if it's a browser approval request
  const isBrowserApproval = useMemo(() => {
    if (!askEvent) return false;
    const msg = (typeof askEvent === "object" && "message" in askEvent ? (askEvent as any).message : "") || "";
    const approvalPatterns = [
      /confirm.*browser/i, /autorizar/i, /approve/i, /permission/i,
      /take.*over/i, /login/i, /captcha/i, /wide.*research/i,
      /sensitive.*operation/i, /confirm.*operation/i, /browser.*operation/i,
    ];
    return approvalPatterns.some(p => p.test(msg));
  }, [askEvent]);

  // Extract text from various content formats (Manus API returns content as array of {type, text} objects)
  // NEVER returns raw JSON — always extracts human-readable text
  function extractText(content: unknown, depth = 0): string {
    if (depth > 5) return ""; // Prevent infinite recursion
    if (!content) return "";
    if (typeof content === "string") {
      // If the string looks like JSON, try to parse and extract text from it
      const trimmed = content.trim();
      if ((trimmed.startsWith("{") || trimmed.startsWith("[")) && depth < 3) {
        try {
          const parsed = JSON.parse(trimmed);
          const extracted = extractText(parsed, depth + 1);
          return extracted || "";
        } catch {
          return content;
        }
      }
      return content;
    }
    if (Array.isArray(content)) {
      return content.map((c: any) => {
        if (typeof c === "string") return c;
        // Manus API content item format: {type: "output_text", text: "..."}
        if (c?.type === "output_text" && c?.text) return c.text;
        // Generic text fields
        if (typeof c?.text === "string" && c.text.length > 0) return c.text;
        // Content field (string or nested)
        if (c?.content) return extractText(c.content, depth + 1);
        // Message field
        if (typeof c?.message === "string" && c.message.length > 0) return c.message;
        // If it has role + content (conversation message), extract content
        if (c?.role && c?.content) return extractText(c.content, depth + 1);
        // Skip file-only items, status objects, etc.
        if (c?.type === "output_file" || c?.fileUrl || c?.fileName) return "";
        if (c?.status || c?.id) return ""; // Skip metadata objects
        return "";
      }).filter(Boolean).join("\n");
    }
    if (typeof content === "object" && content !== null) {
      const obj = content as Record<string, unknown>;
      // Direct text fields — recursively extract if they look like JSON
      if (typeof obj.text === "string" && obj.text.length > 0) {
        return extractText(obj.text, depth + 1);
      }
      if (typeof obj.message === "string" && obj.message.length > 0) {
        return extractText(obj.message, depth + 1);
      }
      // Content field — can be string or array
      if (typeof obj.content === "string" && obj.content.length > 0) {
        return extractText(obj.content, depth + 1);
      }
      if (Array.isArray(obj.content)) return extractText(obj.content, depth + 1);
      // Output field (for task-level objects)
      if (Array.isArray(obj.output)) return extractText(obj.output, depth + 1);
      // NEVER return JSON.stringify — return empty instead
      return "";
    }
    return String(content);
  }

  // Extract file attachments from Manus API content array
  function extractAttachments(msg: any): any[] | undefined {
    const files: any[] = [];
    // Check explicit attachments field
    if (msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0) {
      files.push(...msg.attachments);
    }
    // Check content array for file items (output_file, fileUrl/fileName)
    if (Array.isArray(msg.content)) {
      for (const c of msg.content) {
        if (c?.type === "output_file" && (c?.fileUrl || c?.url)) {
          files.push({
            file_name: c.fileName || c.file_name || "file",
            url: c.fileUrl || c.url || "",
            size_bytes: c.size_bytes || 0,
            mime_type: c.mimeType || c.mime_type || "",
          });
        } else if (c?.fileUrl || c?.fileName) {
          files.push({
            file_name: c.fileName || c.file_name || "file",
            url: c.fileUrl || c.url || "",
            size_bytes: 0,
            mime_type: c.mimeType || c.mime_type || "",
          });
        }
      }
    }
    return files.length > 0 ? files : undefined;
  }

  const allEvents: TaskEvent[] = useMemo(() => {
    const events: TaskEvent[] = [];
    if (dbEvents) {
      for (const ev of dbEvents) {
        events.push({ type: ev.eventType as TaskEvent["type"], message: ev.message || undefined, progressType: ev.progressType || undefined, stopReason: ev.stopReason || undefined, attachments: ev.attachments as TaskEvent["attachments"], timestamp: ev.createdAt });
      }
    }
    const lastDbTimestamp = events.length > 0 ? events[events.length - 1].timestamp : 0;
    for (const ev of sseEvents) {
      if (ev.type === "connected" || ev.type === "heartbeat") continue;
      if (ev.timestamp > lastDbTimestamp) events.push(ev);
    }
    return events;
  }, [dbEvents, sseEvents]);

  const conversationMessages: ConversationMessage[] = useMemo(() => {
    const messages: ConversationMessage[] = [];
    const seenContent = new Set<string>();

    // Detect system prompts and internal metadata that should not be displayed
    function isSystemPrompt(text: string): boolean {
      const first500 = text.substring(0, 500).toLowerCase();
      const patterns = [
        "yos brain", "boot obrigatório", "sequência de inicialização",
        "constitution.md", "state.json", "brain-os.md",
        "## tabela de roteamento", "## vault de credenciais",
        "agente yos:", "[agente yos:", "system_prompt",
        "você é a megan", "você é megan", "assistente ia do ecossistema",
        "## regras de ouro", "## ferramentas disponíveis",
        "manus-mcp-cli", "manus-render-diagram",
        "vou inicializar o yos", "inicializar o yos brain",
        "sequência de boot", "iniciando a sequência",
        "brain_vault_pass", "vault de credenciais",
        "passo 1: gh repo clone", "ymonetize-brain",
      ];
      // Also detect raw JSON arrays/objects that leaked through (handles pretty-printed with spaces)
      const stripped = first500.replace(/\s/g, "");
      if (stripped.startsWith('[{"') || stripped.startsWith('[{\\"') || stripped.startsWith('{"id"') || stripped.startsWith('{"role"')) {
        return true;
      }
      return patterns.some(p => first500.includes(p));
    }

    // Clean content: remove internal prefixes and metadata
    function cleanContent(text: string): string {
      let cleaned = text;
      // Remove [custom], [system], [internal] prefixes
      cleaned = cleaned.replace(/^\s*\[(custom|system|internal|context|boot)\]\s*/gi, "");
      // Remove YOS BRAIN boot headers if they leaked
      cleaned = cleaned.replace(/^##\s*YOS BRAIN[\s\S]{0,200}?(?=\n[^#])/i, "");
      return cleaned.trim();
    }

    function addMessage(role: string, content: string, attachments?: any, timestamp?: number) {
      if (!content || content.trim().length === 0) return;
      // Filter out system prompts entirely
      if (isSystemPrompt(content)) return;
      // Clean the content
      const cleaned = cleanContent(content);
      if (!cleaned || cleaned.length === 0) return;
      // Deduplication: check first 100 chars
      const key = `${role}:${cleaned.substring(0, 100)}`;
      if (seenContent.has(key)) return;
      seenContent.add(key);
      messages.push({ role: role as "user" | "assistant", content: cleaned, attachments, timestamp });
    }

    // 1. Prompt inicial do usuário (skip if output already contains the user prompt)
    const userPromptText = extractText(currentTask.prompt);

    // 2. Parse output array (formato Manus API: [{id, role, content: [{type, text}], ...}])
    const output = currentTask.output;
    let hasUserPromptInOutput = false;

    // Helper to parse a single message from the output array
    function parseOutputMessage(msg: any) {
      if (!msg || typeof msg !== "object") return;
      const role = msg.role;
      if (role !== "user" && role !== "assistant") return;

      const text = extractText(msg.content);
      const attachments = extractAttachments(msg);
      const timestamp = msg.created_at
        ? (typeof msg.created_at === "number"
          ? (msg.created_at < 1e12 ? msg.created_at * 1000 : msg.created_at)
          : Number(msg.created_at) * 1000)
        : undefined;

      if (role === "user" && text && userPromptText && text.substring(0, 80) === userPromptText.substring(0, 80)) {
        hasUserPromptInOutput = true;
      }

      // Only add if there's actual text content (skip empty messages)
      if (text && text.trim().length > 0) {
        addMessage(role, text, attachments, timestamp);
      } else if (attachments && attachments.length > 0) {
        // Message with only attachments
        addMessage(role, "[Arquivos anexados]", attachments, timestamp);
      }
    }

    // Helper: try to parse a string as JSON array of Manus messages
    function tryParseAsManusMessages(str: string): boolean {
      try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.role) {
          for (const msg of parsed) parseOutputMessage(msg);
          return true;
        }
      } catch { /* not JSON */ }
      return false;
    }

    if (Array.isArray(output) && output.length > 0) {
      for (const msg of output) {
        parseOutputMessage(msg);
      }
    } else if (output && typeof output === "string") {
      // Try to parse as Manus messages array first
      if (!tryParseAsManusMessages(output)) {
        const text = extractText(output);
        if (text) addMessage("assistant", text);
      }
    } else if (output && typeof output === "object" && !Array.isArray(output)) {
      const outObj = output as Record<string, unknown>;
      // Check if it has a messages/output array nested inside
      if (Array.isArray(outObj.messages)) {
        for (const msg of outObj.messages as any[]) parseOutputMessage(msg);
      } else if (Array.isArray(outObj.output)) {
        for (const msg of outObj.output as any[]) parseOutputMessage(msg);
      } else if (typeof outObj.text === "string" && outObj.text.trim().startsWith("[")) {
        // Webhook format: { text: "[{...}]" } — try to parse as Manus messages
        if (!tryParseAsManusMessages(outObj.text)) {
          const text = extractText(outObj);
          if (text) addMessage("assistant", text, (outObj.attachments as any));
        }
      } else {
        const text = extractText(outObj);
        if (text) addMessage("assistant", text, (outObj.attachments as any));
      }
    }

    // Add user prompt if not already in output
    if (!hasUserPromptInOutput && userPromptText) {
      messages.unshift({ role: "user", content: userPromptText, timestamp: currentTask.createdAt });
    }

    // 3. Complementar com taskEvents (user_reply e respostas do agente)
    if (allEvents.length > 0) {
      for (const ev of allEvents) {
        if (ev.progressType === "user_reply" && ev.message) {
          const content = ev.message.replace(/^Resposta enviada:\s*/i, "");
          addMessage("user", content, undefined, ev.timestamp);
        } else if (
          (ev.type as string) === "task_output" ||
          (ev.type === "task_progress" && ev.progressType === "agent_response" && ev.message)
        ) {
          const attachments = (ev as any).attachments?.map((a: any) => ({
            file_name: a.file_name || a.fileName || "file",
            url: a.url || "",
            size_bytes: a.size_bytes || a.sizeBytes || 0,
          }));
          addMessage("assistant", extractText(ev.message || ""), attachments, ev.timestamp);
        }
      }
    }

    // Sort by timestamp, preserving original insertion order for messages without timestamps
    // Strategy: assign synthetic timestamps to messages without one, based on their position
    // relative to neighboring messages that DO have timestamps
    const indexed = messages.map((m, i) => ({ ...m, _idx: i }));
    const sorted = indexed.sort((a, b) => {
      const ta = a.timestamp || 0;
      const tb = b.timestamp || 0;
      // Both have timestamps: sort chronologically
      if (ta > 0 && tb > 0) return ta - tb;
      // Neither has timestamp: preserve insertion order
      if (ta === 0 && tb === 0) return a._idx - b._idx;
      // One has timestamp, other doesn't: preserve insertion order
      // (messages from output array come first, events come after)
      return a._idx - b._idx;
    });
    return sorted;
  }, [currentTask.prompt, currentTask.output, currentTask.createdAt, allEvents]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [conversationMessages.length]);
  useEffect(() => { timelineEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [allEvents.length]);

  // Quick approval/rejection helpers
  function handleApprove() {
    replyMutation.mutate({ taskId: task.id, message: "Sim, autorizado. Pode prosseguir." });
  }
  function handleReject() {
    replyMutation.mutate({ taskId: task.id, message: "Não, não autorizo. Cancele essa operação." });
  }

  return (
    <div className="flex flex-col h-[calc(100vh-11rem)] bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-border flex items-center gap-3 bg-card/80 backdrop-blur-sm shrink-0">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          {isRenaming ? (
            <form
              onSubmit={(e) => { e.preventDefault(); if (renameValue.trim()) renameMutation.mutate({ taskId: task.id, title: renameValue.trim() }); }}
              className="flex items-center gap-2"
            >
              <input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="flex-1 px-2 py-1 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
                placeholder="Nome da task..."
                autoFocus
              />
              <button type="submit" disabled={renameMutation.isPending || !renameValue.trim()} className="p-1.5 text-primary hover:bg-primary/10 rounded-lg disabled:opacity-40">
                {renameMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              </button>
              <button type="button" onClick={() => { setIsRenaming(false); setRenameValue(task.title || ""); }} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg">
                <X className="w-3.5 h-3.5" />
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-2 group">
              <p className="text-sm font-medium text-foreground truncate">{renameValue || currentTask.prompt}</p>
              <button
                onClick={() => { setRenameValue(renameValue || currentTask.prompt.slice(0, 100)); setIsRenaming(true); }}
                className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-all"
                title="Renomear task"
              >
                <Edit3 className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${statusColors[currentTask.status] || ""}`}>
              <StatusIcon className={`w-2.5 h-2.5 inline mr-0.5 ${currentTask.status === "running" ? "animate-spin" : ""}`} />
              {statusLabels[currentTask.status]}
            </span>
            {currentTask.creditUsage && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Zap className="w-2.5 h-2.5" /> {currentTask.creditUsage} créditos</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isActive && isConnected && (
            <span className="flex items-center gap-1.5 text-[10px] text-success"><span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" /> Ao vivo</span>
          )}
          {taskUrl && (
            <a
              href={taskUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border text-muted-foreground hover:text-foreground border-border hover:border-primary/30 hover:bg-primary/5 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Abrir no Manus
            </a>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Chat + Timeline */}
        <div className="flex flex-col overflow-hidden flex-1">
          {/* Chat messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {conversationMessages.map((msg, i) => (
              <MessageBubble key={i} msg={msg} isLast={i === conversationMessages.length - 1} />
            ))}

            {/* Browser Approval Card */}
            {isWaitingForReply && isBrowserApproval && (
              <div className="border-2 border-yellow-500/40 bg-yellow-500/5 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm font-semibold text-yellow-500">Aprovação Necessária</span>
                </div>
                <p className="text-sm text-foreground/80">
                  {(askEvent as any)?.message || "A Megan precisa da sua autorização para continuar."}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleApprove}
                    disabled={replyMutation.isPending}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/80 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-40"
                  >
                    {replyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                    Autorizar
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={replyMutation.isPending}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600/80 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-40"
                  >
                    <Square className="w-4 h-4" />
                    Negar
                  </button>
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Timeline (always visible, collapsible) */}
          <div className="border-t border-border shrink-0">
            <button
              onClick={() => setShowTimeline(!showTimeline)}
              className="w-full flex items-center justify-between px-4 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Radio className={`w-3 h-3 ${isActive ? "text-primary animate-pulse" : ""}`} />
                Timeline {allEvents.length > 0 && `(${allEvents.length})`}
              </span>
              {showTimeline ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            </button>
            {showTimeline && (
              <div className="max-h-40 overflow-y-auto px-4 pb-2 space-y-1">
                {allEvents.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-1">{isActive ? "Aguardando eventos..." : "Nenhum evento"}</p>
                ) : (
                  allEvents.map((ev, i) => <TimelineEvent key={i} event={ev} isLast={i === allEvents.length - 1} />)
                )}
                <div ref={timelineEndRef} />
              </div>
            )}
          </div>

          {/* Reply input - always visible for tasks with manusTaskId */}
          {(isWaitingForReply || currentTask.status === "completed" || currentTask.status === "failed") && (
            <div className="border-t border-border p-3 shrink-0 space-y-2">
              {/* Continuation banner for completed/failed tasks */}
              {!isWaitingForReply && (currentTask.status === "completed" || currentTask.status === "failed") && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/20 rounded-lg">
                  <RefreshCw className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs text-primary font-medium">Continuar conversa — envie um novo prompt para reativar esta task</span>
                </div>
              )}
              {/* Image Preview Modal */}
              {previewImage && (
                <div
                  className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                  onClick={() => setPreviewImage(null)}
                >
                  <div className="relative max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                    <img src={previewImage.url} alt={previewImage.name} className="max-w-full max-h-[85vh] object-contain" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3">
                      <p className="text-white text-sm font-medium truncate">{previewImage.name}</p>
                    </div>
                    <button onClick={() => setPreviewImage(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Uploaded files in reply — with image previews */}
              {replyFiles.length > 0 && (
                <div className="flex items-start gap-2 flex-wrap p-2.5 bg-background/50 border border-border rounded-xl">
                  {replyFiles.map((f) => {
                    const isImg = f.type.startsWith("image/");
                    return (
                      <div key={f.fileId} className="relative group rounded-lg border border-border overflow-hidden bg-secondary/50 hover:border-primary/30 transition-all">
                        {isImg && f.previewUrl ? (
                          <div className="relative w-20 h-20 cursor-pointer" onClick={() => !f.uploading && setPreviewImage({ url: f.previewUrl!, name: f.name })}>
                            <img src={f.previewUrl} alt={f.name} className="w-full h-full object-cover" />
                            {f.uploading && (
                              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <Loader2 className="w-4 h-4 text-white animate-spin" />
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1.5 py-0.5">
                              <p className="text-[9px] text-white truncate">{f.name}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 px-2.5 py-2 min-w-[120px] max-w-[160px]">
                            {f.uploading ? <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0" /> : <FileText className="w-4 h-4 text-blue-400 shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] font-medium text-foreground truncate">{f.name}</p>
                              <p className="text-[9px] text-muted-foreground">{f.uploading ? "Enviando..." : f.error ? <span className="text-red-400">Erro</span> : `${(f.size / 1024).toFixed(1)}KB`}</p>
                            </div>
                          </div>
                        )}
                        {!f.uploading && (
                          <button
                            onClick={() => removeReplyFile(f.fileId)}
                            className="absolute top-1 right-1 w-4 h-4 rounded-full bg-background/90 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 hover:border-red-500 hover:text-white text-foreground"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {replyFiles.length > 1 && !replyFiles.some(f => f.uploading) && (
                    <button
                      onClick={() => { replyFiles.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); }); setReplyFiles([]); }}
                      className="text-[10px] text-muted-foreground hover:text-destructive flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-destructive/10 transition-colors self-start"
                    >
                      <X className="w-3 h-3" />Limpar
                    </button>
                  )}
                </div>
              )}

              {isUploadingReply && (
                <div className="flex items-center gap-2 text-[11px] text-primary">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Enviando {replyFiles.filter(f => f.uploading).length} arquivo(s)...</span>
                </div>
              )}

              {/* Advanced options for reply */}
              {showReplyAdvanced && (
                <div className="p-3 bg-background/50 border border-border rounded-xl space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Agente YOS</label>
                      <select
                        value={replyAgent}
                        onChange={(e) => setReplyAgent(e.target.value)}
                        className="w-full px-2 py-1.5 bg-background border border-input rounded-lg text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
                      >
                        <option value="">Auto</option>
                        <option value="megan">Megan</option>
                        <option value="publish_manager">Publish Manager</option>
                        <option value="funnel_builder">Funnel Builder</option>
                        <option value="content_processor">Content Processor</option>
                        <option value="strategist">Strategist</option>
                        <option value="critic">Critic</option>
                        <option value="ads_manager">Ads Manager</option>
                        <option value="monitor">Monitor</option>
                        <option value="creatives">Creatives</option>
                        <option value="seo_specialist">SEO Specialist</option>
                        <option value="data_analyst">Data Analyst</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Skills</label>
                      <div className="max-h-20 overflow-y-auto space-y-0.5 bg-background border border-input rounded-lg p-1.5">
                        {["site-deployer", "content-gen", "seo-audit", "ad-launcher", "image-gen", "analytics"].map(s => (
                          <label key={s} className="flex items-center gap-1.5 text-[10px] text-foreground cursor-pointer hover:bg-secondary/50 rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={replySkills.includes(s)}
                              onChange={(e) => {
                                if (e.target.checked) setReplySkills(prev => [...prev, s]);
                                else setReplySkills(prev => prev.filter(x => x !== s));
                              }}
                              className="rounded border-border text-primary focus:ring-primary/50"
                            />
                            {s}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Conectores Manus</label>
                      <div className="max-h-20 overflow-y-auto space-y-0.5 bg-background border border-input rounded-lg p-1.5">
                        {(manusConnectors as any)?.connectors ? Object.entries((manusConnectors as any).connectors).map(([key, uuid]) => (
                          <label key={key} className="flex items-center gap-1.5 text-[10px] text-foreground cursor-pointer hover:bg-secondary/50 rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={replyConnectors.includes(uuid as string)}
                              onChange={(e) => {
                                if (e.target.checked) setReplyConnectors(prev => [...prev, uuid as string]);
                                else setReplyConnectors(prev => prev.filter(x => x !== uuid));
                              }}
                              className="rounded border-border text-primary focus:ring-primary/50"
                            />
                            {key.replace(/_/g, " ")}
                          </label>
                        )) : <p className="text-[10px] text-muted-foreground">Carregando...</p>}
                      </div>
                    </div>
                  </div>
                  {(replyAgent || replySkills.length > 0 || replyConnectors.length > 0) && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {replyAgent && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 flex items-center gap-1">
                          <Bot className="w-2.5 h-2.5" />{replyAgent}
                          <button onClick={() => setReplyAgent("")} className="hover:text-destructive"><X className="w-2.5 h-2.5" /></button>
                        </span>
                      )}
                      {replySkills.map(s => (
                        <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20 flex items-center gap-1">
                          <Zap className="w-2.5 h-2.5" />{s}
                          <button onClick={() => setReplySkills(prev => prev.filter(x => x !== s))} className="hover:text-destructive"><X className="w-2.5 h-2.5" /></button>
                        </span>
                      ))}
                      {replyConnectors.length > 0 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20">
                          <Plug className="w-2.5 h-2.5 inline mr-1" />{replyConnectors.length} conector(es)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); handleReplySubmit(); }} className="flex items-end gap-2">
                <input
                  ref={replyFileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleReplyFileUpload}
                  accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json,.xlsx,.xls,.pptx,.zip,.mp3,.mp4,.webm"
                />
                <button
                  type="button"
                  onClick={() => replyFileInputRef.current?.click()}
                  disabled={isUploadingReply}
                  className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary disabled:opacity-40"
                  title="Anexar arquivo"
                >
                  {isUploadingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => setShowReplyAdvanced(!showReplyAdvanced)}
                  className={`p-1.5 transition-colors rounded-lg ${showReplyAdvanced ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
                  title="Agentes, Skills e Conectores"
                >
                  <Settings2 className="w-4 h-4" />
                </button>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={currentTask.status === "completed" || currentTask.status === "failed" ? "Continuar conversa... (envie novo prompt, arquivos ou selecione agentes)" : "Responda à Megan... (anexe arquivos, selecione agentes/skills)"}
                  rows={2}
                  className="flex-1 px-3 py-2 bg-background border border-input rounded-xl text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring/50"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleReplySubmit(); } }}
                />
                <button
                  type="submit"
                  disabled={replyMutation.isPending || (!replyText.trim() && replyFiles.length === 0)}
                  className="self-end px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-40 flex items-center gap-2"
                >
                  {replyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Enviar
                </button>
              </form>
            </div>
          )}
        </div>


      </div>
    </div>
  );
}

// ─── Tasks Tab ───────────────────────────────────────────────────────────────

function InlineMiniTimeline({ taskId, isActive }: { taskId: string; isActive: boolean }) {
  const { data: events, isLoading } = trpc.brain.taskEvents.useQuery({ taskId }, { refetchInterval: isActive ? 5000 : false });

  if (isLoading) return <div className="animate-pulse space-y-2"><div className="h-2 bg-muted rounded w-3/4" /><div className="h-2 bg-muted rounded w-1/2" /></div>;
  if (!events || events.length === 0) return <p className="text-xs text-muted-foreground">{isActive ? "Aguardando eventos..." : "Nenhum evento registrado"}</p>;

  const recentEvents = events.slice(-5);
  return (
    <div className="space-y-1.5">
      {recentEvents.map((ev, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
            ev.eventType === "task_stopped" ? ev.stopReason === "finish" ? "bg-success" : "bg-yellow-500" : ev.eventType === "task_created" ? "bg-primary" : "bg-muted-foreground"
          }`} />
          <p className="text-xs text-muted-foreground line-clamp-1">{ev.message || "Processando..."}</p>
          <span className="text-[10px] text-muted-foreground/50 shrink-0 ml-auto">
            {new Date(ev.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      ))}
      {events.length > 5 && <p className="text-[10px] text-muted-foreground/50">+{events.length - 5} eventos anteriores</p>}
    </div>
  );
}

function TasksTab() {
  const [command, setCommand] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [showFunnelModal, setShowFunnelModal] = useState(false);
  const [showCreativesModal, setShowCreativesModal] = useState(false);
  const [taskMode, setTaskMode] = useState<"standard" | "browser" | "research">("standard");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string>("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedConnectors, setSelectedConnectors] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ fileId: string; name: string }>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();

  const { data: brainSkills } = trpc.brain.skills.useQuery();
  const { data: manusConnectors } = trpc.brain.getManusConnectors.useQuery();

  const { data: tasks, isLoading } = trpc.brain.listTasks.useQuery(undefined, { refetchInterval: 15000 });

  const sendCommand = trpc.brain.sendCommand.useMutation({
    onSuccess: (newTask) => { setCommand(""); utils.brain.listTasks.invalidate(); setSelectedTaskId(newTask.id); },
  });

  const sendBrowserCommand = trpc.brain.sendBrowserCommand.useMutation({
    onSuccess: (newTask) => { setCommand(""); utils.brain.listTasks.invalidate(); setSelectedTaskId(newTask.id); },
  });

  const sendResearchCommand = trpc.brain.sendResearchCommand.useMutation({
    onSuccess: (newTask) => { setCommand(""); utils.brain.listTasks.invalidate(); setSelectedTaskId(newTask.id); },
  });

  const isSubmitting = sendCommand.isPending || sendBrowserCommand.isPending || sendResearchCommand.isPending;

  const uploadFile = trpc.brain.uploadFile.useMutation();

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        const result = await uploadFile.mutateAsync({ fileName: file.name });
        // Upload file to the presigned URL
        await fetch(result.upload_url, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type || "application/octet-stream" },
        });
        setUploadedFiles(prev => [...prev, { fileId: result.file_id, name: file.name }]);
      }
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!command.trim() || isSubmitting) return;
    const fileIds = uploadedFiles.map(f => f.fileId);
    const connectors = selectedConnectors.length > 0 ? selectedConnectors : undefined;
    const agent = selectedAgent || undefined;
    const skills = selectedSkills.length > 0 ? selectedSkills : undefined;

    if (taskMode === "browser") {
      sendBrowserCommand.mutate({ command: command.trim() });
    } else if (taskMode === "research") {
      sendResearchCommand.mutate({ command: command.trim() });
    } else {
      sendCommand.mutate({
        command: command.trim(),
        fileIds: fileIds.length > 0 ? fileIds : undefined,
        connectors,
        selectedAgent: agent,
        selectedSkills: skills,
      });
    }
    setUploadedFiles([]);
  }

  function handleShortcutSuccess(taskId: string) {
    utils.brain.listTasks.invalidate();
    setSelectedTaskId(taskId);
  }

  const selectedTask = tasks?.find((t) => t.id === selectedTaskId);
  if (selectedTask) return <TaskDetail task={selectedTask} onBack={() => setSelectedTaskId(null)} />;

  const activeTasks = tasks?.filter((t) => t.status === "pending" || t.status === "running") || [];
  const completedTasks = tasks?.filter((t) => t.status === "completed") || [];

  const suggestions = [
    { icon: Globe, text: "Criar site de nicho sobre seguros de vida" },
    { icon: BarChart3, text: "Analisar performance dos sites ativos" },
    { icon: Terminal, text: "Gerar conteúdo SEO para todos os sites" },
    { icon: Activity, text: "Executar auditoria completa do sistema" },
  ];

  const shortcuts = [
    { icon: Globe, label: "Criar Funil", description: "Deploy rápido de site de nicho", color: "text-primary", bgColor: "bg-primary/10", borderColor: "border-primary/20 hover:border-primary/40", onClick: () => setShowFunnelModal(true) },
    { icon: Palette, label: "Criar Criativos", description: "Gerar criativos + estratégia de ads", color: "text-orange-500", bgColor: "bg-orange-500/10", borderColor: "border-orange-500/20 hover:border-orange-500/40", onClick: () => setShowCreativesModal(true) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {activeTasks.length > 0 && (
          <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-full font-medium">
            <Radio className="w-3 h-3 animate-pulse" /> {activeTasks.length} ativa{activeTasks.length !== 1 ? "s" : ""}
          </span>
        )}
        {completedTasks.length > 0 && (
          <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-success/10 text-success border border-success/20 rounded-full font-medium">
            <CheckCircle2 className="w-3 h-3" /> {completedTasks.length} concluída{completedTasks.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {shortcuts.map((s) => (
          <button key={s.label} onClick={s.onClick} className={`flex items-center gap-3 p-3.5 bg-card border rounded-xl text-left transition-all hover:shadow-md active:scale-[0.98] ${s.borderColor}`}>
            <div className={`w-9 h-9 rounded-xl ${s.bgColor} flex items-center justify-center shrink-0`}>
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{s.label}</p>
              <p className="text-[10px] text-muted-foreground">{s.description}</p>
            </div>
          </button>
        ))}
      </div>

      <CreateFunnelModal open={showFunnelModal} onClose={() => setShowFunnelModal(false)} onSuccess={handleShortcutSuccess} />
      <CreateCreativesModal open={showCreativesModal} onClose={() => setShowCreativesModal(false)} onSuccess={handleShortcutSuccess} />

      <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-4 hover:border-primary/20 transition-colors">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-primary" />
          <label className="text-sm font-semibold text-foreground">Novo Comando</label>
          <div className="flex items-center gap-1 ml-auto">
            {(["standard", "browser", "research"] as const).map((mode) => {
              const modeLabels = { standard: "Padrão", browser: "Browser", research: "Pesquisa" };
              const modeIcons = { standard: Sparkles, browser: Globe, research: BarChart3 };
              const ModeIcon = modeIcons[mode];
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setTaskMode(mode)}
                  className={`flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full border transition-all ${
                    taskMode === mode
                      ? "bg-primary/10 text-primary border-primary/30 font-medium"
                      : "text-muted-foreground border-border hover:border-primary/20 hover:text-foreground"
                  }`}
                >
                  <ModeIcon className="w-2.5 h-2.5" />
                  {modeLabels[mode]}
                </button>
              );
            })}
          </div>
        </div>
        {taskMode !== "standard" && (
          <div className={`flex items-center gap-2 px-3 py-2 mb-3 rounded-lg text-[11px] ${
            taskMode === "browser"
              ? "bg-blue-500/10 border border-blue-500/20 text-blue-400"
              : "bg-violet-500/10 border border-violet-500/20 text-violet-400"
          }`}>
            {taskMode === "browser" ? (
              <><Globe className="w-3 h-3" /> Modo Browser: A task terá acesso a navegador web (Playwright + My Browser). Ideal para automação web, scraping, e interações com sites.</>
            ) : (
              <><BarChart3 className="w-3 h-3" /> Modo Pesquisa: Wide Research com Perplexity + Firecrawl + Browser. Ideal para pesquisas profundas e coleta de dados.</>
            )}
          </div>
        )}
        <div className="flex gap-3">
          <div className="flex-1 space-y-2">
            <textarea
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              placeholder={
                taskMode === "browser" ? "Descreva a tarefa web (ex: acessar site X e extrair dados...)" :
                taskMode === "research" ? "Descreva o que pesquisar (ex: tendências de nicho de seguros 2026...)" :
                "Descreva o que você quer que o Brain faça..."
              }
              className="w-full px-4 py-3 bg-background border border-input rounded-xl text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-primary/40 transition-colors placeholder:text-muted-foreground/60"
              rows={2}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
            />
            {/* File uploads & Advanced toggle */}
            <div className="flex items-center gap-2 flex-wrap">
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json,.xlsx" />
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/20 transition-all">
                {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
                Anexar Arquivo
              </button>
              <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition-all ${
                showAdvanced ? "bg-primary/10 text-primary border-primary/30" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/20"
              }`}>
                <Settings2 className="w-3 h-3" />
                Avançado
              </button>
              {uploadedFiles.map((f) => (
                <span key={f.fileId} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  <FileText className="w-2.5 h-2.5" />{f.name}
                  <button type="button" onClick={() => setUploadedFiles(prev => prev.filter(x => x.fileId !== f.fileId))} className="hover:text-destructive ml-0.5">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
          <button type="submit" disabled={isSubmitting || !command.trim()} className="self-end px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:opacity-90 active:scale-95 transition-all disabled:opacity-40 flex items-center gap-2 shadow-lg shadow-primary/20">
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Enviar
          </button>
        </div>

        {/* Advanced Panel: Agent, Skills, Connectors */}
        {showAdvanced && (
          <div className="mt-3 p-3 bg-background/50 border border-border rounded-xl space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Agent Selection */}
              <div>
                <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Agente YOS</label>
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
                >
                  <option value="">Auto (Megan decide)</option>
                  <option value="megan">Megan (Orquestradora)</option>
                  <option value="funnel_builder">Funnel Builder</option>
                  <option value="content_processor">Content Processor</option>
                  <option value="strategist">Strategist</option>
                  <option value="critic">Critic</option>
                  <option value="ads_manager">Ads Manager</option>
                  <option value="monitor">Monitor</option>
                  <option value="performance_optimizer">Performance Optimizer</option>
                  <option value="creatives">Creatives</option>
                  <option value="publish_manager">Publish Manager</option>
                  <option value="seo_specialist">SEO Specialist</option>
                  <option value="data_analyst">Data Analyst</option>
                </select>
              </div>

              {/* Skills Selection */}
              <div>
                <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Skills YOS</label>
                <div className="max-h-32 overflow-y-auto space-y-1 bg-background border border-input rounded-lg p-2">
                  {(brainSkills && typeof brainSkills === "object" && "skills" in (brainSkills as any)
                    ? ((brainSkills as any).skills || []) as Array<{ name: string; description?: string }>
                    : [
                        { name: "site-deployer", description: "Deploy de sites" },
                        { name: "content-gen", description: "Geração de conteúdo" },
                        { name: "seo-audit", description: "Auditoria SEO" },
                        { name: "ad-launcher", description: "Lançamento de ads" },
                        { name: "image-gen", description: "Geração de imagens" },
                        { name: "video-gen", description: "Geração de vídeos" },
                        { name: "analytics", description: "Analytics" },
                        { name: "domain-manager", description: "Gerenciamento de domínios" },
                      ]
                  ).map((skill: any) => (
                    <label key={skill.name} className="flex items-center gap-2 text-[11px] text-foreground cursor-pointer hover:bg-secondary/50 rounded px-1 py-0.5">
                      <input
                        type="checkbox"
                        checked={selectedSkills.includes(skill.name)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedSkills(prev => [...prev, skill.name]);
                          else setSelectedSkills(prev => prev.filter(s => s !== skill.name));
                        }}
                        className="rounded border-border text-primary focus:ring-primary/50"
                      />
                      <span>{skill.name}</span>
                      {skill.description && <span className="text-muted-foreground/60 ml-auto text-[10px]">{skill.description}</span>}
                    </label>
                  ))}
                </div>
              </div>

              {/* Connectors Selection */}
              <div>
                <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Conectores Manus</label>
                <div className="max-h-32 overflow-y-auto space-y-1 bg-background border border-input rounded-lg p-2">
                  {(manusConnectors?.connectors || []).map((c: any) => (
                    <label key={c.id} className="flex items-center gap-2 text-[11px] text-foreground cursor-pointer hover:bg-secondary/50 rounded px-1 py-0.5">
                      <input
                        type="checkbox"
                        checked={selectedConnectors.includes(c.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedConnectors(prev => [...prev, c.id]);
                          else setSelectedConnectors(prev => prev.filter(x => x !== c.id));
                        }}
                        className="rounded border-border text-primary focus:ring-primary/50"
                      />
                      <span>{c.name}</span>
                      <span className="text-muted-foreground/60 ml-auto text-[10px]">{c.category}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {(selectedAgent || selectedSkills.length > 0 || selectedConnectors.length > 0) && (
              <div className="flex items-center gap-2 flex-wrap">
                {selectedAgent && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 flex items-center gap-1">
                    <Bot className="w-2.5 h-2.5" />{selectedAgent}
                    <button type="button" onClick={() => setSelectedAgent("")} className="hover:text-destructive"><X className="w-2.5 h-2.5" /></button>
                  </span>
                )}
                {selectedSkills.map(s => (
                  <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20 flex items-center gap-1">
                    <Zap className="w-2.5 h-2.5" />{s}
                    <button type="button" onClick={() => setSelectedSkills(prev => prev.filter(x => x !== s))} className="hover:text-destructive"><X className="w-2.5 h-2.5" /></button>
                  </span>
                ))}
                {selectedConnectors.map(c => {
                  const conn = manusConnectors?.connectors?.find((x: any) => x.id === c);
                  return (
                    <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20 flex items-center gap-1">
                      <Plug className="w-2.5 h-2.5" />{conn?.name || c}
                      <button type="button" onClick={() => setSelectedConnectors(prev => prev.filter(x => x !== c))} className="hover:text-destructive"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {(sendCommand.error || sendBrowserCommand.error || sendResearchCommand.error) && (
          <p className="text-xs text-destructive mt-2 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" />{(sendCommand.error || sendBrowserCommand.error || sendResearchCommand.error)?.message}</p>
        )}
        {!command && (
          <div className="flex flex-wrap gap-2 mt-3">
            {suggestions.map(({ icon: Icon, text }) => (
              <button key={text} type="button" onClick={() => setCommand(text)} className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 hover:bg-primary/5 transition-all">
                <Icon className="w-3 h-3 text-primary/70" />{text}
              </button>
            ))}
          </div>
        )}
      </form>

      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-foreground">Tarefas Recentes</h2>
          <button onClick={() => utils.brain.listTasks.invalidate()} className="text-muted-foreground hover:text-foreground transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse border border-border rounded-lg p-4">
                <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-muted rounded w-1/4" />
              </div>
            ))}
          </div>
        ) : tasks && tasks.length > 0 ? (
          <div className="space-y-2">
            {tasks.map((task) => {
              const SIcon = statusIcons[task.status] || Clock;
              const isActive = task.status === "pending" || task.status === "running";
              const isExpanded = expandedTasks.has(task.id);
              return (
                <div key={task.id} className={`border rounded-xl transition-all ${isActive ? "border-primary/30 bg-primary/5" : "border-border hover:bg-secondary/50"}`}>
                  <div className="p-3.5 cursor-pointer" onClick={() => setSelectedTaskId(task.id)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground line-clamp-1">{(task as any).title || task.prompt.slice(0, 100)}</p>
                        {(task as any).title && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{task.prompt.slice(0, 120)}</p>}
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${statusColors[task.status] || ""}`}>
                            <SIcon className={`w-3 h-3 inline mr-1 ${task.status === "running" ? "animate-spin" : ""}`} />
                            {statusLabels[task.status] || task.status}
                          </span>
                          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />{new Date(task.createdAt).toLocaleString("pt-BR")}
                          </span>
                          {task.creditUsage && (
                            <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Zap className="w-3 h-3" />{task.creditUsage} créditos</span>
                          )}
                          {isActive && (
                            <span className="text-[11px] text-primary flex items-center gap-1"><Radio className="w-3 h-3 animate-pulse" />Ao vivo</span>
                          )}
                        </div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); const next = new Set(expandedTasks); if (next.has(task.id)) next.delete(task.id); else next.add(task.id); setExpandedTasks(next); }} className="p-1 text-muted-foreground hover:text-foreground transition-colors">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-border px-3.5 py-3 bg-background/50">
                      <InlineMiniTimeline taskId={task.id} isActive={isActive} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-10">
            <BrainCircuit className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
            <h3 className="text-base font-semibold text-foreground mb-1">Nenhuma tarefa ainda</h3>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">Envie um comando acima para começar.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Connectors Tab ──────────────────────────────────────────────────────────

function ConnectorsTab() {
  const { data: connectorsData, isLoading, refetch } = trpc.brain.getConnectors.useQuery();
  const { data: manusConnectors } = trpc.brain.getManusConnectors.useQuery();
  const [selectedConnector, setSelectedConnector] = useState<string | null>(null);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [toolParams, setToolParams] = useState<string>("{}");
  const [toolResult, setToolResult] = useState<any>(null);
  const [toolError, setToolError] = useState<string | null>(null);
  const [showAddConnector, setShowAddConnector] = useState(false);
  const [activeSection, setActiveSection] = useState<"yos" | "manus">("yos");

  // YOS Connectors (editable)
  const { data: yosConnectors, refetch: refetchYos } = trpc.connectors.list.useQuery();
  const [editingConnector, setEditingConnector] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [newConnectorType, setNewConnectorType] = useState<string>("");
  const [newConnectorName, setNewConnectorName] = useState<string>("");
  const [newConnectorConfig, setNewConnectorConfig] = useState<string>("{}");

  const upsertConnector = trpc.connectors.upsert.useMutation({
    onSuccess: () => { refetchYos(); setShowAddConnector(false); setNewConnectorType(""); setNewConnectorName(""); setNewConnectorConfig("{}"); },
  });
  const deleteConnector = trpc.connectors.delete.useMutation({
    onSuccess: () => refetchYos(),
  });
  const seedFromVault = trpc.connectors.seedFromVault.useMutation({
    onSuccess: (data) => { refetchYos(); alert(data.message); },
    onError: (err) => alert("Erro ao importar do Vault: " + err.message),
  });

  const executeTool = trpc.brain.executeConnectorTool.useMutation({
    onSuccess: (data) => { setToolResult(data); setToolError(null); },
    onError: (err) => { setToolError(err.message); setToolResult(null); },
  });

  const handleExecuteTool = () => {
    if (!selectedConnector || !selectedTool) return;
    try {
      const params = JSON.parse(toolParams);
      executeTool.mutate({ connector: selectedConnector, tool: selectedTool, params });
    } catch {
      setToolError("JSON inválido nos parâmetros");
    }
  };

  const yosConnectorsList = (yosConnectors as any)?.connectors || [];

  const handleSaveConnector = (id: string) => {
    const conn = yosConnectorsList.find((c: any) => c.id === id);
    if (!conn) return;
    upsertConnector.mutate({
      connectorId: id,
      type: conn.type,
      name: editForm.name || conn.name,
      config: editForm.config ? JSON.parse(editForm.config) : conn.config,
    });
    setEditingConnector(null);
    setEditForm({});
  };

  const handleAddConnector = () => {
    if (!newConnectorType || !newConnectorName) return;
    try {
      const config = JSON.parse(newConnectorConfig);
      const connectorId = newConnectorName.toLowerCase().replace(/[^a-z0-9]/g, "_");
      upsertConnector.mutate({
        connectorId,
        type: newConnectorType as "database" | "github" | "railway" | "api_key" | "custom",
        name: newConnectorName,
        config,
      });
    } catch {
      // Invalid JSON
    }
  };

  const connectors = Array.isArray(connectorsData) ? connectorsData : (connectorsData as any)?.connectors || [];
  const connectorIcons: Record<string, typeof Plug> = {
    github: GitBranch, railway: Activity, database: Database, web: Globe,
    cloudflare: Globe, cloudflare_api: Globe, google_drive: FileText,
    playwright: Globe, my_browser: Globe, firecrawl: Globe,
    supabase: Database, stripe: Zap, perplexity: BarChart3,
    notion: FileText, gmail: MessageCircle,
  };

  const categoryLabels: Record<string, string> = {
    development: "Desenvolvimento", infrastructure: "Infraestrutura",
    productivity: "Produtividade", browser: "Navegador",
    research: "Pesquisa", database: "Banco de Dados",
    payments: "Pagamentos", seo: "SEO", analytics: "Analytics",
    crm: "CRM", advertising: "Publicidade", social: "Social",
    communication: "Comunicação", other: "Outros",
  };

  const connectorTemplates = [
    { type: "database", name: "PostgreSQL", icon: Database, config: { host: "", port: "5432", database: "", user: "", password: "" } },
    { type: "database", name: "MySQL", icon: Database, config: { host: "", port: "3306", database: "", user: "", password: "" } },
    { type: "github", name: "GitHub Repo", icon: GitBranch, config: { token: "", repo: "", owner: "" } },
    { type: "railway", name: "Railway", icon: Activity, config: { token: "", projectId: "" } },
    { type: "api", name: "API Custom", icon: Globe, config: { baseUrl: "", apiKey: "", headers: {} } },
    { type: "cloudflare", name: "Cloudflare", icon: Globe, config: { apiToken: "", accountId: "" } },
  ];

  return (
    <div className="space-y-4">
      {/* Section Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-0.5">
          <button onClick={() => setActiveSection("yos")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
            activeSection === "yos" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          }`}>
            <Database className="w-3 h-3 inline mr-1" />Conectores YOS
          </button>
          <button onClick={() => setActiveSection("manus")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
            activeSection === "manus" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          }`}>
            <Plug className="w-3 h-3 inline mr-1" />Conectores Manus
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => seedFromVault.mutate()}
            disabled={seedFromVault.isPending}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-success/10 text-success border border-success/20 rounded-lg hover:bg-success/20 transition-colors disabled:opacity-50"
            title="Importar conectores do Vault YOS (GitHub, Railway, Voyage AI, DB)"
          >
            {seedFromVault.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            Vault YOS
          </button>
          <button onClick={() => setShowAddConnector(!showAddConnector)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-lg hover:bg-primary/20 transition-colors">
            <Plus className="w-3 h-3" /> Adicionar
          </button>
          <button onClick={() => { refetch(); refetchYos(); }} className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary/50 transition-colors">
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Add New Connector Form */}
      {showAddConnector && (
        <div className="bg-card border border-primary/20 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Adicionar Novo Conector</h3>
            <button onClick={() => setShowAddConnector(false)} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Templates */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {connectorTemplates.map((t) => (
              <button
                key={`${t.type}-${t.name}`}
                onClick={() => {
                  setNewConnectorType(t.type);
                  setNewConnectorName(t.name);
                  setNewConnectorConfig(JSON.stringify(t.config, null, 2));
                }}
                className={`flex items-center gap-2 p-3 rounded-lg border transition-all text-left ${
                  newConnectorType === t.type && newConnectorName === t.name
                    ? "bg-primary/10 border-primary/30 text-primary"
                    : "bg-secondary/30 border-border hover:border-primary/20 text-foreground"
                }`}
              >
                <t.icon className="w-4 h-4 shrink-0" />
                <span className="text-xs font-medium">{t.name}</span>
              </button>
            ))}
          </div>

          {newConnectorType && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Tipo</label>
                  <input
                    value={newConnectorType}
                    onChange={(e) => setNewConnectorType(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Nome</label>
                  <input
                    value={newConnectorName}
                    onChange={(e) => setNewConnectorName(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Configuração (JSON)</label>
                <textarea
                  value={newConnectorConfig}
                  onChange={(e) => setNewConnectorConfig(e.target.value)}
                  rows={5}
                  className="w-full bg-background border border-input rounded-lg px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 resize-none"
                />
              </div>
              <button
                onClick={handleAddConnector}
                disabled={upsertConnector.isPending}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
              >
                {upsertConnector.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Salvar Conector
              </button>
            </div>
          )}
        </div>
      )}

      {/* YOS Connectors Section */}
      {activeSection === "yos" && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Conectores YOS são usados pelo agente Megan no chat e nas tasks. Configure tokens, dados de banco e permissões de repositórios.</p>

          {yosConnectorsList.length === 0 ? (
            <div className="text-center py-10">
              <Database className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
              <h3 className="text-base font-semibold text-foreground mb-1">Nenhum conector YOS</h3>
              <p className="text-xs text-muted-foreground">Clique em "Adicionar" para configurar um conector.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {yosConnectorsList.map((conn: any) => {
                const IconComp = connectorIcons[conn.type] || Plug;
                const isEditing = editingConnector === conn.id;
                return (
                  <div key={conn.id} className="bg-card border border-border rounded-xl p-4 transition-all hover:border-primary/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                          <IconComp className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{conn.name}</p>
                          <p className="text-[11px] text-muted-foreground capitalize">{conn.type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            if (isEditing) {
                              handleSaveConnector(conn.id);
                            } else {
                              setEditingConnector(conn.id);
                              setEditForm({
                                name: conn.name,
                                config: JSON.stringify(conn.config || {}, null, 2),
                              });
                            }
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${
                            isEditing ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                          }`}
                        >
                          {isEditing ? <Save className="w-3.5 h-3.5" /> : <Edit3 className="w-3.5 h-3.5" />}
                        </button>
                        {isEditing && (
                          <button
                            onClick={() => { setEditingConnector(null); setEditForm({}); }}
                            className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary/50 transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteConnector.mutate({ connectorId: conn.id })}
                          className="p-1.5 text-muted-foreground hover:text-destructive rounded-lg hover:bg-destructive/10 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {isEditing ? (
                      <div className="mt-3 pt-3 border-t border-border space-y-2">
                        <div>
                          <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Nome</label>
                          <input
                            value={editForm.name || ""}
                            onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Configuração (JSON)</label>
                          <textarea
                            value={editForm.config || "{}"}
                            onChange={(e) => setEditForm(prev => ({ ...prev, config: e.target.value }))}
                            rows={5}
                            className="w-full bg-background border border-input rounded-lg px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 resize-none"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(conn.config || {}).map(([key, val]) => (
                            <span key={key} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                              {key}: {typeof val === "string" && val.length > 20 ? `${val.slice(0, 8)}...${val.slice(-4)}` : String(val)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Manus Connectors Section */}
      {activeSection === "manus" && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Conectores Manus são usados automaticamente nas tasks via API Manus. Selecione os que deseja habilitar no painel Avançado ao criar tasks.</p>

          {/* Manus Connectors Catalog */}
          {manusConnectors && (manusConnectors as any)?.connectors && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {((manusConnectors as any)?.connectors || []).map((mc: any) => {
                const MIcon = connectorIcons[mc.name] || Plug;
                return (
                  <div key={mc.name} className="flex items-center gap-3 p-3 rounded-lg border bg-secondary/30 border-border hover:border-primary/20 transition-all">
                    <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
                      <MIcon className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground capitalize">{mc.name.replace(/_/g, " ")}</p>
                      <p className="text-[10px] text-muted-foreground">{categoryLabels[mc.category] || mc.category}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
            <p className="text-[11px] text-blue-400 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              Conectores Manus são habilitados por task. Use o painel "Avançado" ao criar uma task para selecionar quais conectores usar.
            </p>
          </div>

          {/* Brain API Connectors (MCP) */}
          {connectors.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Conectores Brain API (MCP)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(Array.isArray(connectors) ? connectors : []).map((conn: any) => {
                  const name = conn.name || conn;
                  const IconComp = connectorIcons[name] || Plug;
                  const isSelected = selectedConnector === name;
                  const tools = conn.tools || [];
                  const isReady = conn.ready !== false;

                  return (
                    <div
                      key={name}
                      className={`bg-card border rounded-xl p-4 transition-all cursor-pointer ${
                        isSelected ? "border-primary/50 ring-1 ring-primary/20" : "border-border hover:border-primary/20"
                      }`}
                      onClick={() => { setSelectedConnector(isSelected ? null : name); setSelectedTool(null); setToolResult(null); setToolError(null); }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isReady ? "bg-primary/10" : "bg-muted/50"}`}>
                            <IconComp className={`w-4 h-4 ${isReady ? "text-primary" : "text-muted-foreground"}`} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground capitalize">{name}</p>
                            <p className="text-[10px] text-muted-foreground">{tools.length} tools</p>
                          </div>
                        </div>
                        <div className={`w-2 h-2 rounded-full ${isReady ? "bg-success" : "bg-muted-foreground"}`} />
                      </div>

                      {isSelected && tools.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-border space-y-1">
                          {tools.map((tool: any) => {
                            const toolName = typeof tool === "string" ? tool : tool.name || tool;
                            return (
                              <button
                                key={toolName}
                                onClick={(e) => { e.stopPropagation(); setSelectedTool(toolName); setToolResult(null); setToolError(null); }}
                                className={`w-full text-left p-2 rounded-lg text-xs transition-all ${
                                  selectedTool === toolName ? "bg-primary/10 text-primary border border-primary/20" : "bg-secondary/30 text-muted-foreground hover:bg-secondary/50"
                                }`}
                              >
                                <Wrench className="w-3 h-3 inline mr-1.5" />{String(toolName).split(".").pop()}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tool Executor */}
      {selectedConnector && selectedTool && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">{selectedConnector}.{String(selectedTool).split(".").pop()}</h3>
            </div>
            <button onClick={handleExecuteTool} disabled={executeTool.isPending} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
              {executeTool.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              Executar
            </button>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">Parâmetros (JSON)</label>
            <textarea
              value={toolParams}
              onChange={(e) => setToolParams(e.target.value)}
              rows={3}
              className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              placeholder='{"key": "value"}'
            />
          </div>
          {toolError && (
            <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{toolError}</p>
            </div>
          )}
          {toolResult && (
            <div className="p-3 bg-primary/5 border border-primary/10 rounded-lg">
              <p className="text-xs font-medium text-primary mb-2">Resultado</p>
              <pre className="text-xs text-foreground overflow-x-auto whitespace-pre-wrap font-mono">{JSON.stringify(toolResult, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Brain Page ─────────────────────────────────────────────────────────

export function Brain() {
  const [activeTab, setActiveTab] = useState<BrainTab>("chat");
  const [selectedModel, setSelectedModel] = useState("auto");

  // Office WebSocket — inicializado aqui para manter conexão persistente entre abas
  const { connectionState, officeState, isConnected, sendChat, requestState, setRestFallback, agentExecEvents, clearAgentExecEvents, executeAgent, stopAgentExecution, executingAgents, agentExecEventsByAgent } = useOfficeWS();

  // REST fallback para chat do office quando WS não conecta
  const sendChatRest = trpc.office.sendChat.useMutation();
  React.useEffect(() => {
    setRestFallback(async (sender: string, content: string, channel: string) => {
      await sendChatRest.mutateAsync({ sender, content, channel });
    });
  }, [setRestFallback, sendChatRest]);

  // Fallback: buscar estado via tRPC se WS não conectar
  const { data: trpcOfficeState } = trpc.office.getState.useQuery(undefined, {
    enabled: !isConnected && activeTab === "office",
    refetchInterval: isConnected ? false : 10000,
  });

  // Unificar estado do office (WS prioritário, tRPC como fallback)
  const resolvedOfficeState = officeState || (trpcOfficeState ? {
    agents: trpcOfficeState.agents as any,
    zones: trpcOfficeState.zones as any,
    chat: trpcOfficeState.chat as any,
    stats: trpcOfficeState.stats as any,
  } : null);

  const tabs: { id: BrainTab; label: string; icon: typeof MessageCircle; description: string }[] = [
    { id: "chat", label: "Chat", icon: MessageCircle, description: "Conversa + Agente + Tasks" },
    { id: "office", label: "Escritório", icon: Building2, description: "Agentes em tempo real" },
    { id: "connectors", label: "Conectores", icon: Plug, description: "GitHub, Railway, DB, Web" },
  ];

  // Contar agentes ativos para badge no tab
  const activeAgentsCount = resolvedOfficeState
    ? Object.values(resolvedOfficeState.agents).filter((a: any) => ["working", "thinking", "speaking"].includes(a.status)).length
    : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
            <BrainCircuit className="w-6 h-6 text-primary" />
            Brain Office
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Centro de comando unificado — chat, agentes e escritório virtual
          </p>
        </div>
        {/* Connection Status */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-card border border-border rounded-lg">
          <span className={`w-2 h-2 rounded-full ${isConnected ? "bg-green-400 animate-pulse" : "bg-muted-foreground/50"}`} />
          <span className="text-[10px] text-muted-foreground">
            {isConnected ? "Tempo Real" : "Polling"}
          </span>
          {activeAgentsCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-primary/10 text-primary text-[10px] font-medium rounded-md">
              {activeAgentsCount} ativo{activeAgentsCount > 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 p-1 bg-secondary/50 border border-border rounded-xl w-fit overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-card text-foreground shadow-sm border border-border"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? "text-primary" : ""}`} />
            {tab.label}
            {tab.id === "office" && activeAgentsCount > 0 && (
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            )}
            <span className="text-[10px] text-muted-foreground hidden lg:inline">{tab.description}</span>
          </button>
        ))}
      </div>

      {activeTab === "chat" && <ChatTab selectedModel={selectedModel} onModelChange={setSelectedModel} />}
      {activeTab === "office" && (
        <div className="bg-card border border-border rounded-xl overflow-hidden" style={{ height: "calc(100vh - 200px)" }}>
          <OfficeView
            officeState={resolvedOfficeState}
            connectionState={connectionState}
            isConnected={isConnected}
            sendChat={sendChat}
            requestState={requestState}
            executeAgent={executeAgent}
            executingAgents={executingAgents}
            agentExecEvents={agentExecEventsByAgent}
          />
        </div>
      )}
      {activeTab === "connectors" && <ConnectorsTab />}
    </div>
  );
}
